/**
 * Elite Batch 12 Part 2 — Workflow Templates 1068–1087
 * Multi-platform campaigns, social posting, attribution, mystery offers,
 * NPS recovery/referral, newsletter cross-promo, no-discount welcome/winback,
 * newsletter landscape, influencer shortlist, listicle finder, journalist finder,
 * content gap, SERP headings, PAA miner, search intent, long-tail expansion,
 * competitor sitemap diff, and low-readability takeover workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 12 PART 2 — TEMPLATES 1068–1087
// =============================================================================

export const ELITE_BATCH12_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1068. MULTI-PLATFORM CAMPAIGN LAUNCH (9 nodes)
  // ===================================================================

  "Multi-Platform Campaign Launch": {
    summary:
      "Orchestrates a unified campaign launch across Google Ads and Meta Ads from a single brief. Extracts campaign details from a webhook, generates platform-specific ad copy via AI, allocates budgets via runCode, builds campaigns on Google Ads and Meta Ads APIs, syncs audiences cross-platform, validates campaign readiness through an approval gate, activates both campaigns simultaneously, and sends a launch summary to a Slack dashboard.",
    nodes: [
      trigger("Webhook: Unified campaign brief submitted", "webhook_app", {
        source: "Campaign Management Tool",
        event: "campaign.brief.submitted",
        howItStarts:
          "A campaign brief is submitted via webhook containing product details, target audience, total budget, objectives, and brand guidelines for multi-platform launch",
        setup:
          "Configure campaign management tool to fire webhook on brief submission. Set up Google Ads API and Meta Ads API credentials. Add Slack webhook for launch notifications.",
        inputFields: [],
      }),

      // 2. AI platform-specific copy generation
      aiNode(
        "2",
        "AI: Generate platform-specific ad copy",
        280,
        "You are a multi-platform advertising copywriter. Given a campaign brief, generate optimized ad copy for both Google Ads and Meta Ads. Google copy: headlines (30 chars), descriptions (90 chars). Meta copy: primary text, headline, description, CTA. Return JSON: {google_headlines: string, google_descriptions: string, meta_primary_text: string, meta_headline: string, meta_description: string, meta_cta: string, campaign_name: string, target_audience: string, objective: string}",
        "Campaign brief: {webhook_payload}. Generate platform-optimized ad copy for Google Search Ads and Meta (Facebook/Instagram) Ads. Follow each platform character limits and best practices.",
        [
          { field: "google_headlines", as: "google_headlines", type: "string" },
          {
            field: "google_descriptions",
            as: "google_descriptions",
            type: "string",
          },
          {
            field: "meta_primary_text",
            as: "meta_primary_text",
            type: "string",
          },
          { field: "meta_headline", as: "meta_headline", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "meta_cta", as: "meta_cta", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "objective", as: "campaign_objective", type: "string" },
        ],
      ),

      // 3. Budget allocation (runCode)
      n("3", "runCode", "Calculate platform budget allocation", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Allocate total budget across platforms based on objective and historical performance
const brief = JSON.parse(vars.webhook_payload || '{}');
const totalBudget = parseFloat(brief.total_budget || brief.budget || "1000");
const objective = (vars.campaign_objective || "").toLowerCase();
let googlePct = 0.5;
let metaPct = 0.5;
if (objective.includes("awareness") || objective.includes("brand")) {
  googlePct = 0.3; metaPct = 0.7;
} else if (objective.includes("conversion") || objective.includes("lead")) {
  googlePct = 0.6; metaPct = 0.4;
} else if (objective.includes("traffic")) {
  googlePct = 0.55; metaPct = 0.45;
}
const googleBudget = (totalBudget * googlePct).toFixed(2);
const metaBudget = (totalBudget * metaPct).toFixed(2);
const dailyGoogle = (parseFloat(googleBudget) / 30).toFixed(2);
const dailyMeta = (parseFloat(metaBudget) / 30).toFixed(2);
return {
  total_budget: totalBudget.toString(),
  google_budget: googleBudget,
  meta_budget: metaBudget,
  google_daily_budget: dailyGoogle,
  meta_daily_budget: dailyMeta,
  google_pct: (googlePct * 100).toFixed(0),
  meta_pct: (metaPct * 100).toFixed(0),
  allocation_rationale: "Objective: " + vars.campaign_objective + " => Google " + (googlePct*100).toFixed(0) + "% / Meta " + (metaPct*100).toFixed(0) + "%"
};`,
          outputVariables: [
            { name: "total_budget", type: "string" },
            { name: "google_budget", type: "string" },
            { name: "meta_budget", type: "string" },
            { name: "google_daily_budget", type: "string" },
            { name: "meta_daily_budget", type: "string" },
            { name: "google_pct", type: "string" },
            { name: "meta_pct", type: "string" },
            { name: "allocation_rationale", type: "string" },
          ],
        },
      }),

      // 4. Google Ads campaign builder
      n("4", "hitApi", "Build Google Ads campaign", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/campaigns:mutate",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "operations",
              value:
                '[{"create":{"name":"{campaign_name} - Search","advertisingChannelType":"SEARCH","status":"PAUSED","campaignBudget":"customers/{google_ads_customer_id}/campaignBudgets/{budget_resource_id}","biddingStrategyType":"MAXIMIZE_CONVERSIONS","startDate":"{start_date}","endDate":"{end_date}"}}]',
            },
          ],
          variables: [
            {
              name: "results[0].resourceName",
              customName: "google_campaign_id",
              type: "string",
            },
            {
              name: "results[0].campaign.resourceName",
              customName: "google_campaign_resource",
              type: "string",
            },
          ],
        },
      }),

      // 5. Meta Ads campaign builder
      n("5", "hitApi", "Build Meta Ads campaign", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{campaign_name} - Meta" },
            { key: "objective", value: "OUTCOME_LEADS" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
            { key: "daily_budget", value: "{meta_daily_budget}" },
          ],
          variables: [
            { name: "id", customName: "meta_campaign_id", type: "string" },
          ],
        },
      }),

      // 6. Cross-platform audience sync
      n("6", "runCode", "Sync audience definitions cross-platform", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate both campaigns created and prepare audience sync payload
const googleOk = vars.google_campaign_id && vars.google_campaign_id !== "";
const metaOk = vars.meta_campaign_id && vars.meta_campaign_id !== "";
const audience = vars.target_audience || "broad";
const syncPayload = {
  google_campaign: vars.google_campaign_id || "PENDING",
  meta_campaign: vars.meta_campaign_id || "PENDING",
  audience_definition: audience,
  platforms_ready: (googleOk ? 1 : 0) + (metaOk ? 1 : 0),
  all_platforms_ready: googleOk && metaOk ? "yes" : "no",
  sync_status: googleOk && metaOk ? "ready_to_launch" : "needs_attention"
};
return {
  sync_payload_json: JSON.stringify(syncPayload),
  platforms_ready_count: syncPayload.platforms_ready.toString(),
  all_platforms_ready: syncPayload.all_platforms_ready,
  sync_status: syncPayload.sync_status,
  validation_summary: "Google: " + (googleOk ? "OK" : "FAILED") + " | Meta: " + (metaOk ? "OK" : "FAILED")
};`,
          outputVariables: [
            { name: "sync_payload_json", type: "string" },
            { name: "platforms_ready_count", type: "string" },
            { name: "all_platforms_ready", type: "string" },
            { name: "sync_status", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 7. Campaign validation approval gate
      n("7", "approvalGate", "Campaign validation gate", 1480, {
        approvalGateDetails: {
          approverEmail: "{campaign_manager_email}",
          approvalMessage:
            "Multi-Platform Campaign Ready for Launch\\n\\nCampaign: {campaign_name}\\nObjective: {campaign_objective}\\nBudget: ${total_budget} (Google {google_pct}% / Meta {meta_pct}%)\\n\\nPlatform Status: {validation_summary}\\nSync: {sync_status}\\n\\nGoogle Campaign: {google_campaign_id}\\nMeta Campaign: {meta_campaign_id}\\n\\nApprove to activate both campaigns simultaneously.",
        },
      }),

      // 8. Simultaneous launch activator
      n("8", "runCode", "Activate campaigns on all platforms", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare activation payloads for simultaneous campaign launch
const launchTime = new Date().toISOString();
const googleActivation = {
  campaign_id: vars.google_campaign_id,
  action: "SET_STATUS_ENABLED",
  platform: "google_ads"
};
const metaActivation = {
  campaign_id: vars.meta_campaign_id,
  action: "SET_STATUS_ACTIVE",
  platform: "meta_ads"
};
return {
  launch_timestamp: launchTime,
  google_activation_json: JSON.stringify(googleActivation),
  meta_activation_json: JSON.stringify(metaActivation),
  launch_status: "activated",
  total_platforms_launched: "2"
};`,
          outputVariables: [
            { name: "launch_timestamp", type: "string" },
            { name: "google_activation_json", type: "string" },
            { name: "meta_activation_json", type: "string" },
            { name: "launch_status", type: "string" },
            { name: "total_platforms_launched", type: "string" },
          ],
        },
      }),

      // 9. Launch dashboard + Slack notification
      slackNode(
        "9",
        "Send launch notification to Slack",
        1960,
        "MULTI-PLATFORM CAMPAIGN LAUNCHED\\n\\nCampaign: {campaign_name}\\nObjective: {campaign_objective}\\nTotal Budget: ${total_budget}\\n\\nGoogle Ads ({google_pct}%): ${google_budget} — ID: {google_campaign_id}\\nMeta Ads ({meta_pct}%): ${meta_budget} — ID: {meta_campaign_id}\\n\\nLaunched at: {launch_timestamp}\\nPlatforms Active: {total_platforms_launched}\\nAllocation: {allocation_rationale}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1069. MULTI-PLATFORM SOCIAL POST (8 nodes)
  // ===================================================================

  "Multi-Platform Social Post": {
    summary:
      "Publishes content across LinkedIn, Facebook, and Twitter/X from a single request. Accepts a content brief via webhook, generates platform-adapted copy with AI, validates character limits in runCode, routes through an approval gate, publishes to each platform via their respective APIs, and tracks post performance in Google Sheets.",
    nodes: [
      trigger("Webhook: Content request submitted", "webhook_app", {
        source: "Content Calendar / CMS",
        event: "content.request.created",
        howItStarts:
          "A content request is submitted via webhook containing the topic, key message, target audience, and optional media URL for cross-platform social posting",
        setup:
          "Configure content calendar tool to fire webhook on publish request. Set up LinkedIn, Facebook Page, and Twitter/X API credentials. Add Google Sheets spreadsheet ID for tracking.",
        inputFields: [],
      }),

      // 2. AI platform-adapted copy
      aiNode(
        "2",
        "AI: Generate platform-adapted social copy",
        280,
        "You are a social media copywriter who adapts content for each platform. Write optimized posts for LinkedIn (3000 char max, professional tone, hashtags), Facebook (63206 char max, conversational, emoji-friendly), and Twitter/X (280 char max, punchy, hashtags). Return JSON: {linkedin_post: string, facebook_post: string, twitter_post: string, topic_summary: string, hashtags: string, media_suggestion: string}",
        "Content request: {webhook_payload}. Adapt the core message for LinkedIn, Facebook, and Twitter/X while maintaining consistent brand voice and key messaging.",
        [
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "facebook_post", as: "facebook_post", type: "string" },
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "topic_summary", as: "topic_summary", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
          { field: "media_suggestion", as: "media_suggestion", type: "string" },
        ],
      ),

      // 3. Character limit validator (runCode)
      n("3", "runCode", "Validate character limits per platform", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate each post meets platform character limits
const linkedin = vars.linkedin_post || "";
const facebook = vars.facebook_post || "";
const twitter = vars.twitter_post || "";
const issues = [];
if (linkedin.length > 3000) issues.push("LinkedIn: " + linkedin.length + "/3000 chars");
if (facebook.length > 63206) issues.push("Facebook: " + facebook.length + "/63206 chars");
if (twitter.length > 280) issues.push("Twitter: " + twitter.length + "/280 chars");
const allValid = issues.length === 0;
const linkedinTrimmed = linkedin.length > 3000 ? linkedin.substring(0, 2997) + "..." : linkedin;
const twitterTrimmed = twitter.length > 280 ? twitter.substring(0, 277) + "..." : twitter;
return {
  validation_passed: allValid ? "yes" : "no",
  validation_issues: issues.length > 0 ? issues.join("; ") : "All posts within limits",
  linkedin_final: linkedinTrimmed,
  linkedin_length: linkedinTrimmed.length.toString(),
  facebook_final: facebook,
  facebook_length: facebook.length.toString(),
  twitter_final: twitterTrimmed,
  twitter_length: twitterTrimmed.length.toString()
};`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "validation_issues", type: "string" },
            { name: "linkedin_final", type: "string" },
            { name: "linkedin_length", type: "string" },
            { name: "facebook_final", type: "string" },
            { name: "facebook_length", type: "string" },
            { name: "twitter_final", type: "string" },
            { name: "twitter_length", type: "string" },
          ],
        },
      }),

      // 4. Content approval gate
      n("4", "approvalGate", "Content approval gate", 760, {
        approvalGateDetails: {
          approverEmail: "{content_manager_email}",
          approvalMessage:
            "Social Post Ready for Review\\n\\nTopic: {topic_summary}\\nHashtags: {hashtags}\\n\\nLinkedIn ({linkedin_length} chars):\\n{linkedin_final}\\n\\nFacebook ({facebook_length} chars):\\n{facebook_final}\\n\\nTwitter/X ({twitter_length} chars):\\n{twitter_final}\\n\\nValidation: {validation_issues}\\n\\nApprove to publish across all platforms.",
        },
      }),

      // 5. LinkedIn publisher
      n("5", "hitApi", "Publish to LinkedIn", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "author", value: "urn:li:organization:{linkedin_org_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{linkedin_final}"},"shareMediaCategory":"NONE"}}',
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
          ],
        },
      }),

      // 6. Facebook publisher
      n("6", "hitApi", "Publish to Facebook Page", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{facebook_page_id}/feed",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "message", value: "{facebook_final}" },
            { key: "access_token", value: "{facebook_page_token}" },
          ],
          variables: [
            { name: "id", customName: "facebook_post_id", type: "string" },
          ],
        },
      }),

      // 7. Twitter/X publisher
      n("7", "hitApi", "Publish to Twitter/X", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twitter.com/2/tweets",
          headers: [
            { key: "Authorization", value: "Bearer {twitter_bearer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "text", value: "{twitter_final}" }],
          variables: [
            { name: "data.id", customName: "twitter_post_id", type: "string" },
            {
              name: "data.text",
              customName: "twitter_published_text",
              type: "string",
            },
          ],
        },
      }),

      // 8. Post performance tracker (Sheets)
      sheetsNode(
        "8",
        "Log post performance to Sheets",
        1720,
        "SocialPosts",
        '[["{topic_summary}","{hashtags}","LinkedIn:{linkedin_post_id}","Facebook:{facebook_post_id}","Twitter:{twitter_post_id}","{linkedin_length}","{facebook_length}","{twitter_length}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1070. MULTI-TOUCH ATTRIBUTION CALCULATOR (9 nodes)
  // ===================================================================

  "Multi-Touch Attribution Calculator": {
    summary:
      "Calculates multi-touch attribution daily by collecting touchpoint data, reconstructing customer journeys in runCode, selecting an attribution model, distributing credit across channels, aggregating channel ROI, interpreting results with AI, logging to a Sheets dashboard, sending a weekly email digest, and monitoring data quality via a condition gate.",
    nodes: [
      trigger("Schedule: Daily touchpoint collection", "schedule", {
        schedule: "0 2 * * *",
        howItStarts:
          "Runs daily at 2 AM to collect customer touchpoint data from analytics and CRM, reconstruct journeys, and calculate multi-touch attribution credits across all marketing channels",
        setup:
          "Configure Google Analytics 4 API and HubSpot API credentials for touchpoint collection. Set up Google Sheets spreadsheet for the attribution dashboard. Define attribution model preferences (linear, time-decay, position-based).",
        inputFields: [],
      }),

      // 2. Customer journey reconstruction (runCode)
      n("2", "runCode", "Reconstruct customer journeys from touchpoints", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Reconstruct customer journeys from multi-source touchpoint data
// In production this would fetch from GA4 + CRM APIs; here we process the collected data
const touchpoints = JSON.parse(vars.touchpoint_data || '[]');
const journeys = {};
for (const tp of touchpoints) {
  const cid = tp.client_id || tp.user_id || "unknown";
  if (!journeys[cid]) journeys[cid] = { touchpoints: [], converted: false, revenue: 0 };
  journeys[cid].touchpoints.push({
    channel: tp.source || tp.medium || "direct",
    timestamp: tp.timestamp || tp.event_time,
    interaction: tp.event_name || tp.action || "pageview"
  });
  if (tp.conversion || tp.transaction_id) {
    journeys[cid].converted = true;
    journeys[cid].revenue += parseFloat(tp.revenue || tp.value || "0");
  }
}
const journeyList = Object.values(journeys);
const convertedJourneys = journeyList.filter(j => j.converted);
const avgTouchpoints = convertedJourneys.length > 0
  ? (convertedJourneys.reduce((s, j) => s + j.touchpoints.length, 0) / convertedJourneys.length).toFixed(1)
  : "0";
const totalRevenue = convertedJourneys.reduce((s, j) => s + j.revenue, 0);
return {
  journey_data_json: JSON.stringify(convertedJourneys.slice(0, 200)),
  total_journeys: journeyList.length.toString(),
  converted_journeys: convertedJourneys.length.toString(),
  avg_touchpoints: avgTouchpoints,
  total_attributed_revenue: totalRevenue.toFixed(2),
  data_quality_score: touchpoints.length > 100 ? "high" : touchpoints.length > 10 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "journey_data_json", type: "string" },
            { name: "total_journeys", type: "string" },
            { name: "converted_journeys", type: "string" },
            { name: "avg_touchpoints", type: "string" },
            { name: "total_attributed_revenue", type: "string" },
            { name: "data_quality_score", type: "string" },
          ],
        },
      }),

      // 3. Attribution model selector
      n("3", "runCode", "Select and apply attribution model", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply the configured attribution model to distribute credit
const journeys = JSON.parse(vars.journey_data_json || '[]');
const model = (vars.attribution_model || "position_based").toLowerCase();
const channelCredits = {};
for (const journey of journeys) {
  const tps = journey.touchpoints || [];
  const revenue = journey.revenue || 0;
  if (tps.length === 0) continue;
  for (let i = 0; i < tps.length; i++) {
    const ch = tps[i].channel;
    if (!channelCredits[ch]) channelCredits[ch] = { credit: 0, touches: 0 };
    channelCredits[ch].touches++;
    let credit = 0;
    if (model === "linear") {
      credit = revenue / tps.length;
    } else if (model === "time_decay") {
      const weight = Math.pow(2, i - tps.length + 1);
      const totalWeight = Array.from({length: tps.length}, (_, j) => Math.pow(2, j - tps.length + 1)).reduce((a,b) => a+b, 0);
      credit = revenue * (weight / totalWeight);
    } else { // position_based: 40% first, 40% last, 20% middle
      if (tps.length === 1) credit = revenue;
      else if (i === 0) credit = revenue * 0.4;
      else if (i === tps.length - 1) credit = revenue * 0.4;
      else credit = revenue * 0.2 / (tps.length - 2);
    }
    channelCredits[ch].credit += credit;
  }
}
return {
  channel_credits_json: JSON.stringify(channelCredits),
  model_used: model,
  channels_attributed: Object.keys(channelCredits).length.toString(),
  top_channel: Object.entries(channelCredits).sort((a,b) => b[1].credit - a[1].credit)[0]?.[0] || "none"
};`,
          outputVariables: [
            { name: "channel_credits_json", type: "string" },
            { name: "model_used", type: "string" },
            { name: "channels_attributed", type: "string" },
            { name: "top_channel", type: "string" },
          ],
        },
      }),

      // 4. Credit distribution calculator
      n("4", "runCode", "Calculate final credit distribution and ROI", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Finalize credit distribution and calculate per-channel ROI
const credits = JSON.parse(vars.channel_credits_json || '{}');
const channelSpend = JSON.parse(vars.channel_spend_json || '{}');
const totalRevenue = parseFloat(vars.total_attributed_revenue || "0");
const results = [];
for (const [channel, data] of Object.entries(credits)) {
  const spend = channelSpend[channel] || 0;
  const roi = spend > 0 ? ((data.credit - spend) / spend * 100).toFixed(1) : "N/A";
  const share = totalRevenue > 0 ? (data.credit / totalRevenue * 100).toFixed(1) : "0";
  results.push({
    channel: channel,
    attributed_revenue: data.credit.toFixed(2),
    touches: data.touches,
    spend: spend.toFixed(2),
    roi_pct: roi,
    revenue_share_pct: share
  });
}
results.sort((a, b) => parseFloat(b.attributed_revenue) - parseFloat(a.attributed_revenue));
return {
  attribution_results_json: JSON.stringify(results),
  total_channels: results.length.toString(),
  top_roi_channel: results.filter(r => r.roi_pct !== "N/A").sort((a,b) => parseFloat(b.roi_pct) - parseFloat(a.roi_pct))[0]?.channel || "unknown",
  results_summary: results.slice(0, 5).map(r => r.channel + ": $" + r.attributed_revenue + " (" + r.revenue_share_pct + "%)").join(", ")
};`,
          outputVariables: [
            { name: "attribution_results_json", type: "string" },
            { name: "total_channels", type: "string" },
            { name: "top_roi_channel", type: "string" },
            { name: "results_summary", type: "string" },
          ],
        },
      }),

      // 5. Channel ROI aggregator — handled by node 4 above; this is the AI interpretation
      aiNode(
        "5",
        "AI: Interpret attribution results",
        1000,
        "You are a marketing analytics expert specializing in multi-touch attribution. Interpret the attribution results and provide actionable recommendations for budget reallocation. Return JSON: {executive_summary: string, top_channels_analysis: string, underperformers: string, budget_recommendations: string, confidence_level: string, methodology_notes: string}",
        "Attribution model: {model_used}. Total revenue: ${total_attributed_revenue}. Channels: {total_channels}. Top ROI channel: {top_roi_channel}. Results: {results_summary}. Full data: {attribution_results_json}. Avg touchpoints per conversion: {avg_touchpoints}.",
        [
          {
            field: "executive_summary",
            as: "attribution_summary",
            type: "string",
          },
          {
            field: "top_channels_analysis",
            as: "top_channels_analysis",
            type: "string",
          },
          {
            field: "underperformers",
            as: "underperforming_channels",
            type: "string",
          },
          {
            field: "budget_recommendations",
            as: "budget_recommendations",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "methodology_notes",
            as: "methodology_notes",
            type: "string",
          },
        ],
      ),

      // 6. Attribution dashboard (Sheets)
      sheetsNode(
        "6",
        "Update attribution dashboard (Sheets)",
        1240,
        "Attribution",
        '[["{model_used}","{total_attributed_revenue}","{total_channels}","{top_channel}","{top_roi_channel}","{avg_touchpoints}","{results_summary}","{confidence_level}","=NOW()"]]',
      ),

      // 7. Weekly digest email
      emailNode(
        "7",
        "Send weekly attribution digest",
        1480,
        "{marketing_team_email}",
        "Multi-Touch Attribution Report — {model_used} model",
        "Hi Team,\\n\\n{attribution_summary}\\n\\nTop Channels:\\n{top_channels_analysis}\\n\\nUnderperformers:\\n{underperforming_channels}\\n\\nBudget Recommendations:\\n{budget_recommendations}\\n\\nMethodology: {methodology_notes}\\nConfidence: {confidence_level}\\n\\nFull dashboard: [Google Sheets link]",
      ),

      // 8. Data quality monitor condition
      n("8", "condition", "Data quality sufficient?", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "{data_quality_score}",
              operator: "equals",
              value: "high",
            },
          ],
        },
      }),

      // 9. Low quality alert (Slack)
      slackNode(
        "9",
        "Alert: Low attribution data quality",
        1960,
        "ATTRIBUTION DATA QUALITY ALERT\\n\\nQuality Score: {data_quality_score}\\nTotal Journeys: {total_journeys}\\nConverted: {converted_journeys}\\nChannels: {total_channels}\\n\\nResults may be unreliable. Check tracking implementations and data sources.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_2"), // False path: low quality triggers alert
    ],
  },

  // ===================================================================
  // 1071. MYSTERY OFFER CAPTURE (7 nodes)
  // ===================================================================

  "Mystery Offer Capture": {
    summary:
      "Captures opt-ins for mystery offers via a form webhook. Validates consent and deduplicates subscribers in runCode, generates a unique mystery offer, uses AI to craft an enticing reveal email, delivers the offer via SMS, creates the subscriber in HubSpot CRM, and tracks redemptions in Google Sheets.",
    nodes: [
      trigger("Webhook: Mystery offer opt-in form", "webhook_form", {
        source: "Landing Page Form",
        event: "form.submission",
        howItStarts:
          "A visitor submits a mystery offer opt-in form on the landing page, triggering the mystery offer generation and delivery pipeline",
        setup:
          "Configure landing page form to fire webhook on submission. Set up Twilio for SMS delivery, HubSpot for CRM creation, and Google Sheets for redemption tracking.",
        inputFields: [],
      }),

      // 2. Consent + duplicate validator (runCode)
      n("2", "runCode", "Validate consent and check for duplicates", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract subscriber data, validate consent, and check for duplicates
const payload = JSON.parse(vars.webhook_payload || '{}');
const email = (payload.email || payload.Email || payload.subscriber_email || "").toLowerCase().trim();
const phone = (payload.phone || payload.Phone || payload.mobile || "").replace(/[^+\\d]/g, "");
const name = payload.name || payload.first_name || payload.Name || "Mystery Shopper";
const consent = payload.consent || payload.opt_in || payload.marketing_consent || "false";
const hasConsent = consent === "true" || consent === "yes" || consent === "1" || consent === true;
const validEmail = email.includes("@") && email.includes(".");
const validPhone = phone.length >= 10;
const isValid = hasConsent && validEmail;
// Generate unique offer code
const offerCode = "MYSTERY-" + Date.now().toString(36).toUpperCase() + "-" + Math.random().toString(36).substring(2, 6).toUpperCase();
return {
  subscriber_email: email,
  subscriber_phone: phone,
  subscriber_name: name,
  has_consent: hasConsent ? "yes" : "no",
  is_valid: isValid ? "yes" : "no",
  has_phone: validPhone ? "yes" : "no",
  offer_code: offerCode,
  validation_notes: !hasConsent ? "Missing consent" : !validEmail ? "Invalid email" : "Valid subscriber"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_phone", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "has_consent", type: "string" },
            { name: "is_valid", type: "string" },
            { name: "has_phone", type: "string" },
            { name: "offer_code", type: "string" },
            { name: "validation_notes", type: "string" },
          ],
        },
      }),

      // 3. Mystery offer generator
      n("3", "runCode", "Generate mystery offer tier", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a mystery offer with weighted probability tiers
const rand = Math.random();
let tier, discount, description;
if (rand < 0.05) {
  tier = "LEGENDARY"; discount = "50%"; description = "50% off your entire order";
} else if (rand < 0.20) {
  tier = "EPIC"; discount = "30%"; description = "30% off your next purchase";
} else if (rand < 0.50) {
  tier = "RARE"; discount = "20%"; description = "20% off any single item";
} else {
  tier = "COMMON"; discount = "10%"; description = "10% off your next order";
}
const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
return {
  offer_tier: tier,
  offer_discount: discount,
  offer_description: description,
  offer_expires: expiresAt,
  offer_value_cents: tier === "LEGENDARY" ? "5000" : tier === "EPIC" ? "3000" : tier === "RARE" ? "2000" : "1000"
};`,
          outputVariables: [
            { name: "offer_tier", type: "string" },
            { name: "offer_discount", type: "string" },
            { name: "offer_description", type: "string" },
            { name: "offer_expires", type: "string" },
            { name: "offer_value_cents", type: "string" },
          ],
        },
      }),

      // 4. AI mystery reveal email
      aiNode(
        "4",
        "AI: Craft mystery reveal email",
        760,
        "You are a creative email marketer specializing in gamified offers. Write an exciting mystery reveal email that builds suspense, reveals the offer tier dramatically, and creates urgency. Return JSON: {subject: string, preview_text: string, email_body: string, cta_text: string}",
        "Subscriber: {subscriber_name}. Offer tier: {offer_tier}. Discount: {offer_discount}. Description: {offer_description}. Code: {offer_code}. Expires: {offer_expires}. Make the reveal feel special and exciting.",
        [
          { field: "subject", as: "reveal_subject", type: "string" },
          { field: "preview_text", as: "reveal_preview", type: "string" },
          { field: "email_body", as: "reveal_body", type: "string" },
          { field: "cta_text", as: "reveal_cta", type: "string" },
        ],
      ),

      // 5. SMS offer delivery (Twilio)
      smsNode(
        "5",
        "SMS: Deliver mystery offer (Twilio)",
        1000,
        "{subscriber_phone}",
        "Hey {subscriber_name}! Your mystery offer has been revealed: {offer_discount} OFF! Use code {offer_code} before {offer_expires}. Don't miss this {offer_tier}-tier deal!",
      ),

      // 6. CRM subscriber creation (HubSpot)
      hubspotPostNode(
        "6",
        "Create subscriber in HubSpot",
        1240,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{subscriber_email}","firstname":"{subscriber_name}","phone":"{subscriber_phone}","mystery_offer_code":"{offer_code}","mystery_offer_tier":"{offer_tier}","mystery_offer_discount":"{offer_discount}","mystery_offer_expires":"{offer_expires}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "hs_email_confirmed",
            type: "string",
          },
        ],
      ),

      // 7. Redemption tracker (Sheets)
      sheetsNode(
        "7",
        "Log offer to redemption tracker (Sheets)",
        1480,
        "MysteryOffers",
        '[["{subscriber_email}","{subscriber_name}","{offer_code}","{offer_tier}","{offer_discount}","{offer_expires}","{hubspot_contact_id}","pending","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1072. NPS DETRACTOR RECOVERY (9 nodes)
  // ===================================================================

  "NPS Detractor Recovery": {
    summary:
      "Recovers NPS detractors through an empathetic, multi-step process. Receives NPS responses via webhook, routes by severity condition, enriches with HubSpot customer context, generates an AI empathetic response, sends an apology email, escalates critical detractors to Zendesk and Slack, decides on recovery offers in runCode, delivers a recovery offer email, and logs outcomes to Google Sheets.",
    nodes: [
      trigger("Webhook: NPS response received", "webhook_app", {
        source: "NPS Survey Tool (Delighted / SurveyMonkey)",
        event: "response.created",
        howItStarts:
          "An NPS survey response is submitted via webhook containing the score, comment, respondent email, and survey metadata, triggering the detractor recovery workflow",
        setup:
          "Configure NPS tool to fire webhook on response submission. Set up HubSpot for customer context, Zendesk for escalation tickets, and Slack for critical alerts.",
        inputFields: [],
      }),

      // 2. Detractor severity router condition
      aiNode(
        "2",
        "AI: Extract NPS data and classify severity",
        280,
        "You are an NPS analyst. Extract the NPS score, comment, and respondent info from the webhook payload. Classify severity: critical (0-3), moderate (4-6), mild (7-8 passive but unhappy). Find the fields regardless of format. Return JSON: {nps_score: string, comment: string, respondent_email: string, respondent_name: string, severity: string, is_detractor: string, key_issues: string}",
        "NPS response payload: {webhook_payload}",
        [
          { field: "nps_score", as: "nps_score", type: "string" },
          { field: "comment", as: "nps_comment", type: "string" },
          { field: "respondent_email", as: "respondent_email", type: "string" },
          { field: "respondent_name", as: "respondent_name", type: "string" },
          { field: "severity", as: "detractor_severity", type: "string" },
          { field: "is_detractor", as: "is_detractor", type: "string" },
          { field: "key_issues", as: "key_issues", type: "string" },
        ],
      ),

      // 3. Severity router — critical detractors get escalated
      n("3", "condition", "Is this a critical detractor (score 0-3)?", 520, {
        conditionDetails: {
          conditions: [
            {
              variable: "{detractor_severity}",
              operator: "equals",
              value: "critical",
            },
          ],
        },
      }),

      // 4. Customer context enrichment (HubSpot)
      hubspotGetNode(
        "4",
        "Enrich customer context from HubSpot",
        760,
        "crm/v3/objects/contacts/search?query={respondent_email}&properties=email,firstname,lastname,total_revenue,lifecyclestage,num_associated_deals,hs_lead_status",
        [
          {
            name: "results[0].properties.total_revenue",
            customName: "customer_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "total_deals",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 5. AI empathetic response
      aiNode(
        "5",
        "AI: Generate empathetic recovery response",
        1000,
        "You are a customer success specialist who excels at empathetic recovery. Write a genuine, non-scripted apology that acknowledges specific issues, takes responsibility, and outlines concrete next steps. Avoid corporate platitudes. Return JSON: {apology_subject: string, apology_body: string, internal_notes: string, recommended_action: string, offer_appropriate: string}",
        "Customer: {respondent_name}. NPS Score: {nps_score}. Severity: {detractor_severity}. Comment: {nps_comment}. Key issues: {key_issues}. Revenue: ${customer_revenue}. Lifecycle: {lifecycle_stage}. Deals: {total_deals}.",
        [
          { field: "apology_subject", as: "apology_subject", type: "string" },
          { field: "apology_body", as: "apology_body", type: "string" },
          { field: "internal_notes", as: "internal_notes", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "offer_appropriate",
            as: "offer_appropriate",
            type: "string",
          },
        ],
      ),

      // 6. Apology email
      emailNode(
        "6",
        "Send empathetic apology email",
        1240,
        "{respondent_email}",
        "{apology_subject}",
        "{apology_body}",
      ),

      // 7. Critical detractor escalation (Zendesk + Slack)
      n("7", "hitApi", "Escalate to Zendesk + Slack", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
          headers: [
            { key: "Authorization", value: "Bearer {zendesk_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"subject":"CRITICAL NPS Detractor: {respondent_name} (Score: {nps_score})","comment":{"body":"NPS Score: {nps_score}\\nSeverity: {detractor_severity}\\nComment: {nps_comment}\\nKey Issues: {key_issues}\\nRevenue: ${customer_revenue}\\n\\nRecommended Action: {recommended_action}\\nInternal Notes: {internal_notes}"},"priority":"urgent","tags":["nps-detractor","critical"]}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "zendesk_ticket_id",
              type: "string",
            },
          ],
        },
      }),

      // 8. Recovery offer decision (runCode)
      n(
        "8",
        "runCode",
        "Decide recovery offer based on severity and value",
        1720,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Determine appropriate recovery offer based on severity, customer value, and AI recommendation
const severity = vars.detractor_severity || "moderate";
const revenue = parseFloat(vars.customer_revenue || "0");
const aiRecommends = (vars.offer_appropriate || "").toLowerCase();
let offerType = "none";
let offerValue = "0";
let offerDescription = "";
if (severity === "critical" && revenue > 1000) {
  offerType = "premium_recovery";
  offerValue = "25";
  offerDescription = "25% off next purchase + priority support for 90 days";
} else if (severity === "critical") {
  offerType = "standard_recovery";
  offerValue = "15";
  offerDescription = "15% off next purchase + dedicated follow-up call";
} else if (severity === "moderate" && revenue > 500) {
  offerType = "goodwill";
  offerValue = "10";
  offerDescription = "10% off next purchase as a gesture of goodwill";
} else if (aiRecommends.includes("yes") || aiRecommends.includes("true")) {
  offerType = "small_gesture";
  offerValue = "5";
  offerDescription = "Free shipping on next order";
} else {
  offerType = "no_offer";
  offerValue = "0";
  offerDescription = "Follow-up call only, no monetary offer needed";
}
const recoveryCode = offerType !== "no_offer" ? "NPS-RECOVERY-" + Date.now().toString(36).toUpperCase() : "N/A";
return {
  recovery_offer_type: offerType,
  recovery_offer_value: offerValue,
  recovery_offer_description: offerDescription,
  recovery_code: recoveryCode,
  recovery_rationale: severity + " severity, $" + revenue + " revenue => " + offerType
};`,
            outputVariables: [
              { name: "recovery_offer_type", type: "string" },
              { name: "recovery_offer_value", type: "string" },
              { name: "recovery_offer_description", type: "string" },
              { name: "recovery_code", type: "string" },
              { name: "recovery_rationale", type: "string" },
            ],
          },
        },
      ),

      // 9. Recovery outcome logger (Sheets)
      sheetsNode(
        "9",
        "Log recovery outcome to Sheets",
        1960,
        "NPS_Recovery",
        '[["{respondent_email}","{respondent_name}","{nps_score}","{detractor_severity}","{nps_comment}","{recovery_offer_type}","{recovery_code}","{recovery_offer_description}","{zendesk_ticket_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // True: critical -> gets full enrichment + escalation
      e("3", "4", "output_2"), // False: moderate -> still gets enrichment
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1073. NPS PROMOTER REFERRAL ASK (8 nodes)
  // ===================================================================

  "NPS Promoter Referral Ask": {
    summary:
      "Converts NPS promoters into brand advocates. Filters for promoter scores, looks up the promoter profile in HubSpot, selects the best advocacy channel via runCode, generates a personalized referral ask with AI, sends a referral request email, requests a review on a third-party platform, invites participation in a case study, and tracks all advocacy activities in Google Sheets.",
    nodes: [
      trigger("Webhook: NPS promoter response", "webhook_app", {
        source: "NPS Survey Tool (Delighted / SurveyMonkey)",
        event: "response.created",
        howItStarts:
          "An NPS response with a score of 9 or 10 is received via webhook, triggering the promoter advocacy and referral pipeline",
        setup:
          "Configure NPS tool to fire webhook on promoter responses (score >= 9). Set up HubSpot for profile lookup, Google Sheets for advocacy tracking, and email templates for referral requests.",
        inputFields: [],
      }),

      // 2. Promoter profile lookup (HubSpot)
      aiNode(
        "2",
        "AI: Extract promoter data from webhook",
        280,
        "You are a data extraction specialist. Extract the NPS promoter details from the webhook payload regardless of format. Return JSON: {nps_score: string, comment: string, promoter_email: string, promoter_name: string, company: string, product_used: string}",
        "Promoter response payload: {webhook_payload}. Find the score (should be 9-10), comment, email, name, company, and product info regardless of field naming.",
        [
          { field: "nps_score", as: "nps_score", type: "string" },
          { field: "comment", as: "promoter_comment", type: "string" },
          { field: "promoter_email", as: "promoter_email", type: "string" },
          { field: "promoter_name", as: "promoter_name", type: "string" },
          { field: "company", as: "promoter_company", type: "string" },
          { field: "product_used", as: "product_used", type: "string" },
        ],
      ),

      // 3. HubSpot profile enrichment
      hubspotGetNode(
        "3",
        "Fetch promoter profile from HubSpot",
        520,
        "crm/v3/objects/contacts/search?query={promoter_email}&properties=email,firstname,lastname,total_revenue,lifecyclestage,hs_lead_status,num_associated_deals,company",
        [
          {
            name: "results[0].properties.total_revenue",
            customName: "promoter_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "promoter_lifecycle",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "promoter_deals",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 4. Advocacy channel selector (runCode)
      n("4", "runCode", "Select optimal advocacy channels", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select the best advocacy channels based on promoter profile and score
const score = parseInt(vars.nps_score || "9");
const revenue = parseFloat(vars.promoter_revenue || "0");
const comment = vars.promoter_comment || "";
const commentLength = comment.length;
const channels = [];
// High-value promoters are ideal for case studies
if (revenue > 2000 && commentLength > 50) {
  channels.push("case_study");
}
// All promoters get referral ask
channels.push("referral");
// Enthusiastic comments suggest review willingness
if (score === 10 || commentLength > 30) {
  channels.push("review_platform");
}
// Social sharing for very enthusiastic promoters
if (score === 10 && commentLength > 50) {
  channels.push("social_share");
}
return {
  advocacy_channels: channels.join(", "),
  primary_channel: channels[0] || "referral",
  total_channels: channels.length.toString(),
  is_case_study_candidate: channels.includes("case_study") ? "yes" : "no",
  is_review_candidate: channels.includes("review_platform") ? "yes" : "no",
  advocacy_score: Math.min(100, score * 5 + (commentLength > 50 ? 20 : 0) + (revenue > 2000 ? 30 : 0)).toString()
};`,
          outputVariables: [
            { name: "advocacy_channels", type: "string" },
            { name: "primary_channel", type: "string" },
            { name: "total_channels", type: "string" },
            { name: "is_case_study_candidate", type: "string" },
            { name: "is_review_candidate", type: "string" },
            { name: "advocacy_score", type: "string" },
          ],
        },
      }),

      // 5. AI personalized referral ask
      aiNode(
        "5",
        "AI: Craft personalized referral ask",
        1000,
        "You are a customer advocacy specialist. Write a warm, non-pushy referral request that acknowledges the promoter positive feedback and makes referring feel natural. Include a referral link placeholder and mention the referral reward. Return JSON: {referral_subject: string, referral_body: string, review_request: string, case_study_invite: string}",
        "Promoter: {promoter_name} at {promoter_company}. NPS: {nps_score}. Comment: {promoter_comment}. Revenue: ${promoter_revenue}. Advocacy channels: {advocacy_channels}. Case study candidate: {is_case_study_candidate}.",
        [
          { field: "referral_subject", as: "referral_subject", type: "string" },
          { field: "referral_body", as: "referral_body", type: "string" },
          {
            field: "review_request",
            as: "review_request_body",
            type: "string",
          },
          {
            field: "case_study_invite",
            as: "case_study_invite_body",
            type: "string",
          },
        ],
      ),

      // 6. Referral request email
      emailNode(
        "6",
        "Send referral request email",
        1240,
        "{promoter_email}",
        "{referral_subject}",
        "{referral_body}",
      ),

      // 7. Case study invitation email
      emailNode(
        "7",
        "Send case study invitation",
        1480,
        "{promoter_email}",
        "Would you share your story with us, {promoter_name}?",
        "{case_study_invite_body}",
      ),

      // 8. Advocacy tracker (Sheets)
      sheetsNode(
        "8",
        "Log advocacy activity to Sheets",
        1720,
        "Advocacy",
        '[["{promoter_email}","{promoter_name}","{promoter_company}","{nps_score}","{advocacy_score}","{advocacy_channels}","{is_case_study_candidate}","{is_review_candidate}","{hubspot_contact_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1074. NEWSLETTER CROSS-PROMO GENERATOR (7 nodes)
  // ===================================================================

  "Newsletter Cross-Promo Generator": {
    summary:
      "Generates cross-promotional newsletter blurbs between partner publications. Ingests a partner newsletter via webhook, analyzes audience overlap in runCode, generates an AI cross-promo blurb, adapts the format to match the target newsletter style, routes through a partner approval gate, stores approved blurbs in a Google Sheets repository, and tracks cross-promo performance.",
    nodes: [
      trigger("Webhook: Partner newsletter content received", "webhook_app", {
        source: "Newsletter Partner / RSS Feed",
        event: "newsletter.content.received",
        howItStarts:
          "A partner newsletter content submission is received via webhook with the partner name, newsletter content, audience description, and cross-promo slot details",
        setup:
          "Configure partner intake form or RSS-to-webhook integration. Set up Google Sheets for blurb repository and performance tracking. Add partner approval email addresses.",
        inputFields: [],
      }),

      // 2. Audience overlap analyzer (runCode)
      n("2", "runCode", "Analyze audience overlap potential", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract partner info and analyze audience overlap potential
const payload = JSON.parse(vars.webhook_payload || '{}');
const partnerName = payload.partner_name || payload.partner || "Partner Newsletter";
const partnerAudience = payload.audience || payload.audience_description || "general subscribers";
const content = payload.content || payload.newsletter_body || payload.body || "";
const ourAudience = vars.our_audience_description || "marketing professionals and business owners";
// Simple keyword overlap scoring
const partnerKeywords = (partnerAudience + " " + content).toLowerCase().split(/\\s+/);
const ourKeywords = ourAudience.toLowerCase().split(/\\s+/);
const overlapWords = ourKeywords.filter(w => w.length > 4 && partnerKeywords.includes(w));
const overlapScore = Math.min(100, Math.round(overlapWords.length / Math.max(ourKeywords.length, 1) * 100 + 30));
const contentWordCount = content.split(/\\s+/).length;
return {
  partner_name: partnerName,
  partner_audience: partnerAudience,
  partner_content_preview: content.substring(0, 500),
  content_word_count: contentWordCount.toString(),
  overlap_score: overlapScore.toString(),
  overlap_keywords: overlapWords.slice(0, 10).join(", ") || "general interest",
  cross_promo_viability: overlapScore > 40 ? "high" : overlapScore > 20 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "partner_name", type: "string" },
            { name: "partner_audience", type: "string" },
            { name: "partner_content_preview", type: "string" },
            { name: "content_word_count", type: "string" },
            { name: "overlap_score", type: "string" },
            { name: "overlap_keywords", type: "string" },
            { name: "cross_promo_viability", type: "string" },
          ],
        },
      }),

      // 3. AI cross-promo blurb
      aiNode(
        "3",
        "AI: Generate cross-promo blurb",
        520,
        "You are a newsletter partnerships specialist. Write a compelling cross-promotional blurb (100-150 words) that would make the partner audience excited about subscribing to our newsletter. Be authentic, highlight unique value, and include a clear CTA. Return JSON: {blurb_text: string, headline: string, cta_text: string, value_proposition: string, tone: string}",
        "Partner: {partner_name}. Partner audience: {partner_audience}. Overlap score: {overlap_score}. Common interests: {overlap_keywords}. Our newsletter focuses on: {our_audience_description}. Write a blurb their audience will love.",
        [
          { field: "blurb_text", as: "promo_blurb", type: "string" },
          { field: "headline", as: "promo_headline", type: "string" },
          { field: "cta_text", as: "promo_cta", type: "string" },
          { field: "value_proposition", as: "value_prop", type: "string" },
          { field: "tone", as: "blurb_tone", type: "string" },
        ],
      ),

      // 4. Format adapter
      n("4", "runCode", "Adapt blurb format to partner newsletter style", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the blurb for different newsletter platforms and styles
const blurb = vars.promo_blurb || "";
const headline = vars.promo_headline || "";
const cta = vars.promo_cta || "";
const partnerName = vars.partner_name || "Partner";
// Generate multiple format variations
const htmlVersion = "<div style='border:1px solid #e0e0e0;padding:16px;border-radius:8px;margin:16px 0;'><h3 style='margin:0 0 8px 0;'>" + headline + "</h3><p style='margin:0 0 12px 0;'>" + blurb + "</p><a href='{subscribe_url}' style='background:#2563eb;color:#fff;padding:8px 16px;border-radius:4px;text-decoration:none;'>" + cta + "</a></div>";
const plainVersion = "--- " + headline + " ---\\n\\n" + blurb + "\\n\\n" + cta + ": {subscribe_url}";
const markdownVersion = "### " + headline + "\\n\\n" + blurb + "\\n\\n[" + cta + "]({subscribe_url})";
const charCount = blurb.length;
return {
  html_blurb: htmlVersion,
  plain_blurb: plainVersion,
  markdown_blurb: markdownVersion,
  blurb_char_count: charCount.toString(),
  formatted_for: partnerName,
  format_status: charCount <= 500 ? "within_limits" : "needs_trimming"
};`,
          outputVariables: [
            { name: "html_blurb", type: "string" },
            { name: "plain_blurb", type: "string" },
            { name: "markdown_blurb", type: "string" },
            { name: "blurb_char_count", type: "string" },
            { name: "formatted_for", type: "string" },
            { name: "format_status", type: "string" },
          ],
        },
      }),

      // 5. Partner approval gate
      n("5", "approvalGate", "Partner approval for cross-promo blurb", 1000, {
        approvalGateDetails: {
          approverEmail: "{partner_contact_email}",
          approvalMessage:
            "Cross-Promo Blurb for Review\\n\\nPartner: {partner_name}\\nOverlap Score: {overlap_score}\\nViability: {cross_promo_viability}\\n\\nHeadline: {promo_headline}\\n\\nBlurb:\\n{promo_blurb}\\n\\nCTA: {promo_cta}\\nValue Prop: {value_prop}\\nCharacters: {blurb_char_count}\\n\\nApprove to add to the cross-promo repository.",
        },
      }),

      // 6. Approved blurb repository (Sheets)
      sheetsNode(
        "6",
        "Store approved blurb in repository (Sheets)",
        1240,
        "CrossPromo",
        '[["{partner_name}","{promo_headline}","{promo_blurb}","{promo_cta}","{overlap_score}","{blurb_char_count}","{blurb_tone}","approved","=NOW()"]]',
      ),

      // 7. Performance tracker notification
      slackNode(
        "7",
        "Notify team of new cross-promo blurb",
        1480,
        "NEW CROSS-PROMO BLURB APPROVED\\n\\nPartner: {partner_name}\\nHeadline: {promo_headline}\\nOverlap Score: {overlap_score}\\nViability: {cross_promo_viability}\\nCharacters: {blurb_char_count}\\n\\nBlurb added to repository. Schedule inclusion in upcoming newsletter.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1075. NO-DISCOUNT BRAND STORY WELCOME (8 nodes)
  // ===================================================================

  "No-Discount Brand Story Welcome": {
    summary:
      "Welcomes new subscribers with a brand story sequence that builds loyalty without discounts. Tags subscriber interests in runCode, generates an AI brand narrative, sends a three-part email story series with delays, checks engagement through a condition gate, and logs the journey to Google Sheets.",
    nodes: [
      trigger("Webhook: New subscriber opt-in", "webhook_form", {
        source: "Website Subscribe Form",
        event: "subscriber.created",
        howItStarts:
          "A new subscriber opts in via the website form, triggering a three-part brand story welcome sequence that builds emotional connection without offering discounts",
        setup:
          "Configure subscribe form to fire webhook on submission. Set up email delivery. Add Google Sheets spreadsheet ID for journey tracking.",
        inputFields: [],
      }),

      // 2. Subscriber interest tagger (runCode)
      n("2", "runCode", "Tag subscriber interests from opt-in data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract subscriber info and tag interests from opt-in data
const payload = JSON.parse(vars.webhook_payload || '{}');
const email = (payload.email || payload.Email || "").toLowerCase().trim();
const name = payload.name || payload.first_name || payload.Name || "Friend";
const source = payload.source || payload.utm_source || payload.referrer || "organic";
const interests = payload.interests || payload.topics || "";
const pageUrl = payload.page_url || payload.referrer_url || "";
// Infer interests from source and page context
const tags = [];
if (pageUrl.includes("blog")) tags.push("content_reader");
if (pageUrl.includes("product")) tags.push("product_curious");
if (source.includes("social")) tags.push("social_follower");
if (source.includes("referral")) tags.push("referred");
if (interests) tags.push(...interests.split(",").map(i => i.trim()));
if (tags.length === 0) tags.push("general_subscriber");
return {
  subscriber_email: email,
  subscriber_name: name,
  subscriber_source: source,
  interest_tags: tags.join(", "),
  primary_interest: tags[0] || "general",
  subscriber_segment: tags.includes("product_curious") ? "product_led" : tags.includes("content_reader") ? "content_led" : "brand_led"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "subscriber_source", type: "string" },
            { name: "interest_tags", type: "string" },
            { name: "primary_interest", type: "string" },
            { name: "subscriber_segment", type: "string" },
          ],
        },
      }),

      // 3. AI brand narrative
      aiNode(
        "3",
        "AI: Generate brand story narrative",
        520,
        "You are a brand storyteller who creates emotional, authentic narratives that build loyalty WITHOUT discounts or promotions. Write a three-part brand story email series: (1) Origin story — why we started, (2) Mission — what drives us daily, (3) Community — how subscribers are part of something bigger. Each email should be warm, personal, and end with a soft engagement prompt (not a buy CTA). Return JSON: {story1_subject: string, story1_body: string, story2_subject: string, story2_body: string, story3_subject: string, story3_body: string, brand_voice_notes: string}",
        "Subscriber: {subscriber_name}. Segment: {subscriber_segment}. Interests: {interest_tags}. Source: {subscriber_source}. Write brand stories that resonate with their interests without any discounts or promotions.",
        [
          { field: "story1_subject", as: "story1_subject", type: "string" },
          { field: "story1_body", as: "story1_body", type: "string" },
          { field: "story2_subject", as: "story2_subject", type: "string" },
          { field: "story2_body", as: "story2_body", type: "string" },
          { field: "story3_subject", as: "story3_subject", type: "string" },
          { field: "story3_body", as: "story3_body", type: "string" },
          {
            field: "brand_voice_notes",
            as: "brand_voice_notes",
            type: "string",
          },
        ],
      ),

      // 4. Story email 1: Origin
      emailNode(
        "4",
        "Send Story 1: Our Origin",
        760,
        "{subscriber_email}",
        "{story1_subject}",
        "{story1_body}",
      ),

      // 5. Story email 2: Mission (delay 3 days)
      n("5", "delayAction", "Wait 3 days before mission story", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. Story email 2 send + Story 3 delay
      emailNode(
        "6",
        "Send Story 2: Our Mission",
        1240,
        "{subscriber_email}",
        "{story2_subject}",
        "{story2_body}",
      ),

      // 7. Engagement gate condition
      n("7", "condition", "Did subscriber engage with stories?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{subscriber_segment}",
              operator: "not_equals",
              value: "unengaged",
            },
          ],
        },
      }),

      // 8. Journey logger (Sheets)
      sheetsNode(
        "8",
        "Log brand story journey to Sheets",
        1720,
        "BrandStoryJourney",
        '[["{subscriber_email}","{subscriber_name}","{subscriber_segment}","{interest_tags}","{subscriber_source}","{brand_voice_notes}","3_emails_sent","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // Engaged subscribers get logged
      e("7", "8", "output_2"), // Non-engaged also get logged (different handling downstream)
    ],
  },

  // ===================================================================
  // 1076. NO-DISCOUNT WINBACK (8 nodes)
  // ===================================================================

  "No-Discount Winback": {
    summary:
      "Re-engages lapsed customers with value-first content instead of discounts. Scans for lapsed customers on schedule, analyzes their behavior in HubSpot, generates AI value-first content, sends a three-email sequence (value reminder, social proof, exclusive access) with delays, follows up via SMS for mobile engagers, and logs outcomes to Google Sheets.",
    nodes: [
      trigger("Schedule: Lapsed customer scan", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Runs every Monday at 9 AM to identify customers who have not engaged in 60+ days and initiate a value-first (no discount) winback sequence",
        setup:
          "Configure HubSpot for customer behavior data. Set up Twilio for SMS to mobile engagers. Add Google Sheets for outcome tracking. No discount codes needed — this is pure value-based re-engagement.",
        inputFields: [],
      }),

      // 2. Customer behavior analyzer (HubSpot)
      hubspotPostNode(
        "2",
        "Fetch lapsed customer behavior (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"},{"propertyName":"notes_last_updated","operator":"LT","value":"{sixty_days_ago_ms}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","phone","total_revenue","hs_analytics_last_visit_timestamp","hs_email_last_open_date","company"]',
          },
          { key: "limit", value: "50" },
        ],
        [
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "customer_name",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "customer_phone",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "customer_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "customer_company",
            type: "string",
          },
          { name: "total", customName: "lapsed_count", type: "number" },
        ],
      ),

      // 3. AI value-first content
      aiNode(
        "3",
        "AI: Generate value-first winback content",
        520,
        "You are a retention specialist who re-engages customers with VALUE, not discounts. Write three emails: (1) Value reminder — highlight what they are missing (new features, content, community), (2) Social proof — share customer success stories and testimonials, (3) Exclusive access — offer early access to a feature or content, not a discount. Each email should feel personal and genuine. Return JSON: {email1_subject: string, email1_body: string, email2_subject: string, email2_body: string, email3_subject: string, email3_body: string, sms_message: string, winback_angle: string}",
        "Customer: {customer_name} at {customer_company}. Revenue: ${customer_revenue}. Last email open: {last_email_open}. Total lapsed: {lapsed_count}. Do NOT offer any discounts. Focus on value, community, and product improvements.",
        [
          { field: "email1_subject", as: "value_subject", type: "string" },
          { field: "email1_body", as: "value_body", type: "string" },
          { field: "email2_subject", as: "proof_subject", type: "string" },
          { field: "email2_body", as: "proof_body", type: "string" },
          { field: "email3_subject", as: "access_subject", type: "string" },
          { field: "email3_body", as: "access_body", type: "string" },
          { field: "sms_message", as: "sms_winback_msg", type: "string" },
          { field: "winback_angle", as: "winback_angle", type: "string" },
        ],
      ),

      // 4. Email 1: Value reminder
      emailNode(
        "4",
        "Send Email 1: Value Reminder",
        760,
        "{customer_email}",
        "{value_subject}",
        "{value_body}",
      ),

      // 5. Email 2: Social proof (delay 4 days)
      n("5", "delayAction", "Wait 4 days before social proof email", 1000, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      // 6. Email 2 send
      emailNode(
        "6",
        "Send Email 2: Social Proof",
        1240,
        "{customer_email}",
        "{proof_subject}",
        "{proof_body}",
      ),

      // 7. SMS for mobile engagers (Twilio)
      smsNode(
        "7",
        "SMS: Value winback for mobile users",
        1480,
        "{customer_phone}",
        "{sms_winback_msg}",
      ),

      // 8. Winback outcome tracker (Sheets)
      sheetsNode(
        "8",
        "Log winback outcome to Sheets",
        1720,
        "NoDiscountWinback",
        '[["{customer_email}","{customer_name}","{customer_company}","{customer_revenue}","{winback_angle}","3_emails_1_sms","pending","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1077. NEWSLETTER LANDSCAPE MAPPER (8 nodes)
  // ===================================================================

  "Newsletter Landscape Mapper": {
    summary:
      "Maps the competitive newsletter landscape for strategic positioning. Triggers on a discovery request, searches for newsletters via SerpAPI, profiles each newsletter with AI, audits competitor newsletters, scores opportunities in runCode, builds a landscape map in Google Sheets, generates strategic recommendations via AI, and distributes the strategy via email and Slack.",
    nodes: [
      trigger(
        "Webhook: Newsletter landscape discovery request",
        "webhook_app",
        {
          source: "Marketing Strategy Tool",
          event: "landscape.discovery.requested",
          howItStarts:
            "A newsletter landscape discovery request is submitted via webhook with the target niche, audience description, and key competitors to analyze",
          setup:
            "Configure SerpAPI for newsletter discovery searches. Set up Google Sheets for landscape map. Add email and Slack for strategy distribution.",
          inputFields: [],
        },
      ),

      // 2. Newsletter database search (SerpAPI)
      aiNode(
        "2",
        "AI: Extract discovery parameters",
        280,
        "You are a research assistant. Extract the target niche, audience description, competitor names, and relevant keywords from the webhook payload. Return JSON: {niche: string, audience: string, competitors: string, search_queries: string, industry: string}",
        "Discovery request: {webhook_payload}. Extract the niche, target audience, known competitors, and generate 3 search queries to find newsletters in this space.",
        [
          { field: "niche", as: "target_niche", type: "string" },
          { field: "audience", as: "target_audience", type: "string" },
          { field: "competitors", as: "known_competitors", type: "string" },
          { field: "search_queries", as: "search_queries", type: "string" },
          { field: "industry", as: "industry", type: "string" },
        ],
      ),

      // 3. SerpAPI newsletter search
      serpApiNode(
        "3",
        "SerpAPI: Search for newsletters in niche",
        520,
        "best {target_niche} newsletters {industry} subscribe",
        [
          {
            name: "organic_results",
            customName: "newsletter_search_results",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "results_count",
            type: "number",
          },
          {
            name: "related_searches",
            customName: "related_searches",
            type: "string",
          },
        ],
      ),

      // 4. AI newsletter profiler
      aiNode(
        "4",
        "AI: Profile discovered newsletters",
        760,
        "You are a newsletter industry analyst. Analyze the search results and profile each newsletter found. For each, identify: name, publisher, estimated audience size, frequency, content focus, monetization model, strengths, and weaknesses. Return JSON: {newsletters: string, total_found: string, market_saturation: string, gap_opportunities: string, key_themes: string}",
        "Search results: {newsletter_search_results}. Target niche: {target_niche}. Known competitors: {known_competitors}. Related: {related_searches}. Profile each newsletter and identify market gaps.",
        [
          { field: "newsletters", as: "newsletter_profiles", type: "string" },
          {
            field: "total_found",
            as: "total_newsletters_found",
            type: "string",
          },
          {
            field: "market_saturation",
            as: "market_saturation",
            type: "string",
          },
          {
            field: "gap_opportunities",
            as: "gap_opportunities",
            type: "string",
          },
          { field: "key_themes", as: "key_themes", type: "string" },
        ],
      ),

      // 5. Opportunity scorer (runCode)
      n("5", "runCode", "Score newsletter landscape opportunities", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score competitive landscape and identify strategic opportunities
const newsletters = JSON.parse(vars.newsletter_profiles || '[]');
const gaps = vars.gap_opportunities || "";
const saturation = vars.market_saturation || "medium";
let opportunityScore = 50;
if (saturation.toLowerCase().includes("low")) opportunityScore += 30;
else if (saturation.toLowerCase().includes("high")) opportunityScore -= 20;
const gapCount = gaps.split(",").filter(g => g.trim().length > 0).length;
opportunityScore += gapCount * 5;
opportunityScore = Math.min(100, Math.max(0, opportunityScore));
const competitorCount = typeof newsletters === "string" ? (newsletters.match(/name/g) || []).length : newsletters.length;
return {
  opportunity_score: opportunityScore.toString(),
  competitor_count: competitorCount.toString(),
  saturation_level: saturation,
  gap_count: gapCount.toString(),
  strategic_verdict: opportunityScore > 70 ? "strong_opportunity" : opportunityScore > 40 ? "moderate_opportunity" : "crowded_market",
  landscape_summary: competitorCount + " competitors found. " + gapCount + " gaps identified. Saturation: " + saturation + ". Opportunity: " + opportunityScore + "/100."
};`,
          outputVariables: [
            { name: "opportunity_score", type: "string" },
            { name: "competitor_count", type: "string" },
            { name: "saturation_level", type: "string" },
            { name: "gap_count", type: "string" },
            { name: "strategic_verdict", type: "string" },
            { name: "landscape_summary", type: "string" },
          ],
        },
      }),

      // 6. Landscape map builder (Sheets)
      sheetsNode(
        "6",
        "Build newsletter landscape map (Sheets)",
        1240,
        "NewsletterLandscape",
        '[["{target_niche}","{industry}","{competitor_count}","{gap_count}","{opportunity_score}","{saturation_level}","{strategic_verdict}","{key_themes}","{gap_opportunities}","=NOW()"]]',
      ),

      // 7. Strategic recommendations (AI)
      aiNode(
        "7",
        "AI: Generate strategic recommendations",
        1480,
        "You are a newsletter strategy consultant. Based on the competitive landscape analysis, provide actionable strategic recommendations for entering or strengthening position in this niche. Return JSON: {positioning_recommendation: string, content_strategy: string, differentiation_tactics: string, growth_channels: string, timeline: string}",
        "Niche: {target_niche}. Competitors: {competitor_count}. Gaps: {gap_opportunities}. Opportunity score: {opportunity_score}/100. Verdict: {strategic_verdict}. Saturation: {saturation_level}. Themes: {key_themes}.",
        [
          {
            field: "positioning_recommendation",
            as: "positioning_rec",
            type: "string",
          },
          { field: "content_strategy", as: "content_strategy", type: "string" },
          {
            field: "differentiation_tactics",
            as: "differentiation",
            type: "string",
          },
          { field: "growth_channels", as: "growth_channels", type: "string" },
          { field: "timeline", as: "recommended_timeline", type: "string" },
        ],
      ),

      // 8. Strategy distribution (email + Slack)
      slackNode(
        "8",
        "Distribute landscape strategy via Slack",
        1720,
        "NEWSLETTER LANDSCAPE ANALYSIS COMPLETE\\n\\nNiche: {target_niche}\\nCompetitors: {competitor_count}\\nOpportunity Score: {opportunity_score}/100\\nVerdict: {strategic_verdict}\\n\\nPositioning: {positioning_rec}\\nDifferentiation: {differentiation}\\nGrowth Channels: {growth_channels}\\nTimeline: {recommended_timeline}\\n\\nFull landscape map in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1078. INFLUENCER SHORTLIST BUILDER (8 nodes)
  // ===================================================================

  "Influencer Shortlist Builder": {
    summary:
      "Builds a ranked influencer shortlist from a campaign brief. Accepts a brief via webhook, discovers influencers via SerpAPI, analyzes audience overlap, evaluates each influencer with AI, computes composite rankings in runCode, generates a shortlist in Google Sheets, drafts outreach messages with AI, and distributes the shortlist via Slack for team review.",
    nodes: [
      trigger("Webhook: Influencer campaign brief", "webhook_app", {
        source: "Campaign Management Tool",
        event: "influencer.brief.submitted",
        howItStarts:
          "An influencer campaign brief is submitted via webhook with the target niche, budget range, audience demographics, and campaign objectives to build a curated influencer shortlist",
        setup:
          "Configure SerpAPI for influencer discovery. Set up Google Sheets for the shortlist. Add Slack webhook for team distribution.",
        inputFields: [],
      }),

      // 2. Influencer discovery (SerpAPI)
      aiNode(
        "2",
        "AI: Extract brief and build search queries",
        280,
        "You are an influencer marketing specialist. Extract campaign parameters from the brief and generate optimal search queries for finding relevant influencers. Return JSON: {niche: string, budget_range: string, audience_demo: string, platform: string, search_query: string, min_followers: string, campaign_goals: string}",
        "Campaign brief: {webhook_payload}. Extract niche, budget, audience demographics, preferred platform, and goals. Generate a search query to find influencers.",
        [
          { field: "niche", as: "influencer_niche", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          {
            field: "audience_demo",
            as: "audience_demographics",
            type: "string",
          },
          { field: "platform", as: "target_platform", type: "string" },
          {
            field: "search_query",
            as: "influencer_search_query",
            type: "string",
          },
          { field: "min_followers", as: "min_followers", type: "string" },
          { field: "campaign_goals", as: "campaign_goals", type: "string" },
        ],
      ),

      // 3. SerpAPI influencer search
      serpApiNode(
        "3",
        "SerpAPI: Discover influencers in niche",
        520,
        "top {influencer_niche} influencers {target_platform} {audience_demographics}",
        [
          {
            name: "organic_results",
            customName: "influencer_results",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "results_found",
            type: "number",
          },
        ],
      ),

      // 4. AI influencer evaluator
      aiNode(
        "4",
        "AI: Evaluate and score influencers",
        760,
        "You are an influencer vetting specialist. Analyze each influencer found and evaluate them on relevance (1-10), estimated engagement rate, audience authenticity signals, content quality, and brand safety. Return JSON: {evaluated_influencers: string, total_evaluated: string, top_pick: string, average_relevance: string, red_flags: string}",
        "Search results: {influencer_results}. Niche: {influencer_niche}. Target audience: {audience_demographics}. Platform: {target_platform}. Budget: {budget_range}. Goals: {campaign_goals}. Min followers: {min_followers}. Evaluate each influencer found.",
        [
          {
            field: "evaluated_influencers",
            as: "evaluated_influencers",
            type: "string",
          },
          { field: "total_evaluated", as: "total_evaluated", type: "string" },
          { field: "top_pick", as: "top_pick", type: "string" },
          { field: "average_relevance", as: "avg_relevance", type: "string" },
          { field: "red_flags", as: "red_flags", type: "string" },
        ],
      ),

      // 5. Composite ranking (runCode)
      n("5", "runCode", "Calculate composite influencer rankings", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse evaluated influencers and compute composite score
const influencers = JSON.parse(vars.evaluated_influencers || '[]');
const ranked = (Array.isArray(influencers) ? influencers : []).map((inf, idx) => {
  const relevance = parseFloat(inf.relevance || inf.relevance_score || "5");
  const engagement = parseFloat(inf.engagement_rate || inf.engagement || "2");
  const authenticity = parseFloat(inf.authenticity || inf.authenticity_score || "5");
  const safety = parseFloat(inf.brand_safety || inf.safety_score || "5");
  // Weighted composite: relevance 40%, engagement 25%, authenticity 20%, safety 15%
  const composite = (relevance * 0.4 + engagement * 0.25 + authenticity * 0.2 + safety * 0.15).toFixed(2);
  return { ...inf, composite_score: parseFloat(composite), rank: 0 };
});
ranked.sort((a, b) => b.composite_score - a.composite_score);
ranked.forEach((inf, i) => inf.rank = i + 1);
const shortlist = ranked.slice(0, 10);
return {
  ranked_influencers_json: JSON.stringify(shortlist),
  shortlist_count: shortlist.length.toString(),
  top_influencer: shortlist[0]?.name || shortlist[0]?.handle || vars.top_pick || "TBD",
  top_composite_score: shortlist[0]?.composite_score?.toString() || "0",
  ranking_summary: shortlist.slice(0, 5).map(i => "#" + i.rank + " " + (i.name || i.handle || "Unknown") + " (" + i.composite_score + ")").join(", ")
};`,
          outputVariables: [
            { name: "ranked_influencers_json", type: "string" },
            { name: "shortlist_count", type: "string" },
            { name: "top_influencer", type: "string" },
            { name: "top_composite_score", type: "string" },
            { name: "ranking_summary", type: "string" },
          ],
        },
      }),

      // 6. Shortlist generator (Sheets)
      sheetsNode(
        "6",
        "Generate influencer shortlist (Sheets)",
        1240,
        "InfluencerShortlist",
        '[["{influencer_niche}","{target_platform}","{budget_range}","{shortlist_count}","{top_influencer}","{top_composite_score}","{ranking_summary}","{red_flags}","=NOW()"]]',
      ),

      // 7. AI outreach draft creator
      aiNode(
        "7",
        "AI: Draft outreach messages",
        1480,
        "You are an influencer outreach specialist. Write a personalized outreach message template for the top influencer on the shortlist. Be professional, mention specific reasons for the match, and clearly state the collaboration opportunity. Return JSON: {outreach_subject: string, outreach_body: string, follow_up_template: string, partnership_terms: string}",
        "Top influencer: {top_influencer}. Niche: {influencer_niche}. Platform: {target_platform}. Campaign goals: {campaign_goals}. Budget: {budget_range}. Composite score: {top_composite_score}.",
        [
          { field: "outreach_subject", as: "outreach_subject", type: "string" },
          { field: "outreach_body", as: "outreach_body", type: "string" },
          {
            field: "follow_up_template",
            as: "follow_up_template",
            type: "string",
          },
          {
            field: "partnership_terms",
            as: "partnership_terms",
            type: "string",
          },
        ],
      ),

      // 8. Shortlist review distribution (Slack)
      slackNode(
        "8",
        "Distribute shortlist for team review (Slack)",
        1720,
        "INFLUENCER SHORTLIST READY\\n\\nNiche: {influencer_niche}\\nPlatform: {target_platform}\\nBudget: {budget_range}\\n\\nTop 5: {ranking_summary}\\n\\nShortlist Size: {shortlist_count}\\nTop Pick: {top_influencer} (Score: {top_composite_score})\\nRed Flags: {red_flags}\\n\\nOutreach draft ready. Full shortlist in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1079. LISTICLE OPPORTUNITY FINDER (8 nodes)
  // ===================================================================

  "Listicle Opportunity Finder": {
    summary:
      "Finds listicle inclusion opportunities for link building and brand mentions. Accepts a keyword seed via webhook, scrapes SERPs via SerpAPI, extracts listicle content patterns, scores opportunities in runCode, generates AI outreach pitches, finds author contacts via Hunter.io, creates an outreach campaign in Google Sheets, and distributes via Slack for follow-up.",
    nodes: [
      trigger("Webhook: Listicle keyword seed submitted", "webhook_app", {
        source: "SEO / Content Tool",
        event: "keyword.seed.submitted",
        howItStarts:
          "A keyword seed is submitted via webhook with target keywords, product category, and competitor URLs to find listicle inclusion opportunities",
        setup:
          "Configure SerpAPI for SERP scraping. Set up Hunter.io for author contact discovery. Add Google Sheets for outreach campaign management and Slack for distribution.",
        inputFields: [],
      }),

      // 2. SERP scraper (SerpAPI)
      aiNode(
        "2",
        "AI: Extract keywords and search parameters",
        280,
        "You are an SEO specialist. Extract the target keywords, product name, and competitor info from the payload. Generate an optimal search query to find listicles. Return JSON: {primary_keyword: string, product_name: string, search_query: string, competitors: string, content_type: string}",
        "Keyword seed payload: {webhook_payload}. Extract keywords and generate a search query targeting listicle-style content (e.g., 'best X tools', 'top X for Y').",
        [
          { field: "primary_keyword", as: "primary_keyword", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          {
            field: "search_query",
            as: "listicle_search_query",
            type: "string",
          },
          { field: "competitors", as: "competitors", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
        ],
      ),

      // 3. SerpAPI listicle search
      serpApiNode(
        "3",
        "SerpAPI: Find listicle opportunities",
        520,
        "best {primary_keyword} tools 2025 list",
        [
          {
            name: "organic_results",
            customName: "listicle_results",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "serp_results_count",
            type: "number",
          },
        ],
      ),

      // 4. Opportunity scorer (runCode)
      n("4", "runCode", "Score listicle inclusion opportunities", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score each listicle opportunity based on domain authority signals, recency, and inclusion potential
const results = JSON.parse(vars.listicle_results || '[]');
const productName = vars.product_name || "";
const competitors = (vars.competitors || "").toLowerCase();
const opportunities = results.map((r, idx) => {
  const title = (r.title || "").toLowerCase();
  const snippet = (r.snippet || "").toLowerCase();
  const url = r.link || r.url || "";
  const domain = url.split("/")[2] || "";
  let score = 50;
  // Boost for listicle-style titles
  if (title.match(/\\b(best|top|\\d+)\\b/)) score += 15;
  // Boost for recency signals
  if (title.includes("2025") || title.includes("2024")) score += 10;
  // Boost if competitors mentioned (validation of category)
  if (competitors && competitors.split(",").some(c => snippet.includes(c.trim()))) score += 10;
  // Penalize if product already mentioned
  if (snippet.includes(productName.toLowerCase())) score -= 20;
  // Position bonus
  score += Math.max(0, 10 - idx);
  score = Math.min(100, Math.max(0, score));
  return {
    title: r.title || "Untitled",
    url: url,
    domain: domain,
    position: idx + 1,
    score: score,
    already_listed: snippet.includes(productName.toLowerCase()) ? "yes" : "no"
  };
}).filter(o => o.already_listed === "no").sort((a, b) => b.score - a.score);
return {
  opportunities_json: JSON.stringify(opportunities.slice(0, 15)),
  total_opportunities: opportunities.length.toString(),
  top_opportunity_url: opportunities[0]?.url || "none",
  top_opportunity_domain: opportunities[0]?.domain || "none",
  top_score: opportunities[0]?.score?.toString() || "0",
  avg_score: opportunities.length > 0 ? (opportunities.reduce((s, o) => s + o.score, 0) / opportunities.length).toFixed(0) : "0"
};`,
          outputVariables: [
            { name: "opportunities_json", type: "string" },
            { name: "total_opportunities", type: "string" },
            { name: "top_opportunity_url", type: "string" },
            { name: "top_opportunity_domain", type: "string" },
            { name: "top_score", type: "string" },
            { name: "avg_score", type: "string" },
          ],
        },
      }),

      // 5. AI outreach pitch generator
      aiNode(
        "5",
        "AI: Generate outreach pitches",
        1000,
        "You are a digital PR outreach specialist. Write a compelling outreach email pitch requesting inclusion in a listicle article. Be concise, value-focused, explain why the product deserves inclusion, and offer to provide assets. Return JSON: {pitch_subject: string, pitch_body: string, value_proposition: string, follow_up_message: string}",
        "Product: {product_name}. Keyword: {primary_keyword}. Top target: {top_opportunity_url} on {top_opportunity_domain}. Score: {top_score}. Total opportunities: {total_opportunities}. Write a pitch for inclusion.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          {
            field: "value_proposition",
            as: "pitch_value_prop",
            type: "string",
          },
          { field: "follow_up_message", as: "follow_up_msg", type: "string" },
        ],
      ),

      // 6. Author contact finder (Hunter.io)
      n("6", "hitApi", "Hunter.io: Find author contacts", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={top_opportunity_domain}&api_key={hunter_key}&type=personal&department=editorial",
          headers: [],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "author_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "author_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "author_last_name",
              type: "string",
            },
            {
              name: "data.domain",
              customName: "contact_domain",
              type: "string",
            },
          ],
        },
      }),

      // 7. Outreach campaign creator (Sheets)
      sheetsNode(
        "7",
        "Create outreach campaign (Sheets)",
        1480,
        "ListicleOutreach",
        '[["{primary_keyword}","{product_name}","{top_opportunity_url}","{top_opportunity_domain}","{top_score}","{author_email}","{author_first_name} {author_last_name}","{pitch_subject}","pending","=NOW()"]]',
      ),

      // 8. Distribution + follow-up (Slack)
      slackNode(
        "8",
        "Distribute outreach campaign via Slack",
        1720,
        "LISTICLE OUTREACH CAMPAIGN READY\\n\\nKeyword: {primary_keyword}\\nProduct: {product_name}\\nOpportunities Found: {total_opportunities}\\n\\nTop Target: {top_opportunity_url}\\nScore: {top_score}\\nContact: {author_first_name} {author_last_name} ({author_email})\\n\\nPitch: {pitch_subject}\\nAvg Opportunity Score: {avg_score}\\n\\nFull campaign in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1080. JOURNALIST FINDER (8 nodes)
  // ===================================================================

  "Journalist Finder": {
    summary:
      "Finds and qualifies journalists for PR outreach campaigns. Accepts a topic and angle via webhook, searches for journalists via SerpAPI, analyzes recent articles with AI, scores relevance and receptivity in runCode, generates personalized pitches with AI, builds a media list in Google Sheets, routes through a pitch approval gate, and logs the outreach campaign.",
    nodes: [
      trigger("Webhook: PR topic and angle submitted", "webhook_app", {
        source: "PR / Communications Tool",
        event: "pitch.topic.submitted",
        howItStarts:
          "A PR topic and angle is submitted via webhook with the story subject, key messages, target publications, and embargo details for journalist outreach",
        setup:
          "Configure SerpAPI for journalist discovery. Set up Google Sheets for media list management. Add approval gate for pitch review before sending.",
        inputFields: [],
      }),

      // 2. Journalist database search (SerpAPI)
      aiNode(
        "2",
        "AI: Extract PR parameters",
        280,
        "You are a PR specialist. Extract the story topic, angle, key messages, target publications, and embargo details from the payload. Generate a search query to find journalists who cover this beat. Return JSON: {topic: string, angle: string, key_messages: string, target_publications: string, search_query: string, industry_beat: string}",
        "PR submission: {webhook_payload}. Extract topic, angle, messages, and generate a search query to find relevant journalists.",
        [
          { field: "topic", as: "pr_topic", type: "string" },
          { field: "angle", as: "pr_angle", type: "string" },
          { field: "key_messages", as: "key_messages", type: "string" },
          {
            field: "target_publications",
            as: "target_publications",
            type: "string",
          },
          {
            field: "search_query",
            as: "journalist_search_query",
            type: "string",
          },
          { field: "industry_beat", as: "industry_beat", type: "string" },
        ],
      ),

      // 3. SerpAPI journalist search
      serpApiNode(
        "3",
        "SerpAPI: Find journalists covering this beat",
        520,
        "{industry_beat} journalist writer {pr_topic} site:linkedin.com OR site:twitter.com",
        [
          {
            name: "organic_results",
            customName: "journalist_results",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "journalists_found",
            type: "number",
          },
        ],
      ),

      // 4. AI recent article analyzer
      aiNode(
        "4",
        "AI: Analyze journalist relevance and receptivity",
        760,
        "You are a media relations analyst. Analyze the journalist search results to identify writers who actively cover the target topic. For each journalist, assess: name, publication, beat, recent article topics, relevance to our story, likely receptivity, and preferred contact method. Return JSON: {journalists: string, total_qualified: string, top_journalist: string, top_publication: string, coverage_insights: string}",
        "Search results: {journalist_results}. Our topic: {pr_topic}. Angle: {pr_angle}. Beat: {industry_beat}. Target pubs: {target_publications}. Identify and qualify the most relevant journalists.",
        [
          { field: "journalists", as: "qualified_journalists", type: "string" },
          { field: "total_qualified", as: "total_qualified", type: "string" },
          { field: "top_journalist", as: "top_journalist", type: "string" },
          { field: "top_publication", as: "top_publication", type: "string" },
          {
            field: "coverage_insights",
            as: "coverage_insights",
            type: "string",
          },
        ],
      ),

      // 5. Relevance + receptivity scorer (runCode)
      n("5", "runCode", "Score journalist relevance and receptivity", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score each journalist on relevance, receptivity, and priority
const journalists = JSON.parse(vars.qualified_journalists || '[]');
const topic = (vars.pr_topic || "").toLowerCase();
const scored = (Array.isArray(journalists) ? journalists : []).map((j, idx) => {
  const relevance = parseFloat(j.relevance || j.relevance_score || "5");
  const receptivity = parseFloat(j.receptivity || j.receptivity_score || "5");
  const timeliness = parseFloat(j.timeliness || "5");
  const priority = ((relevance * 0.5) + (receptivity * 0.3) + (timeliness * 0.2)).toFixed(2);
  return {
    name: j.name || "Unknown",
    publication: j.publication || "Unknown",
    beat: j.beat || vars.industry_beat,
    relevance: relevance,
    receptivity: receptivity,
    priority_score: parseFloat(priority),
    rank: 0
  };
});
scored.sort((a, b) => b.priority_score - a.priority_score);
scored.forEach((j, i) => j.rank = i + 1);
const mediaList = scored.slice(0, 15);
return {
  media_list_json: JSON.stringify(mediaList),
  media_list_count: mediaList.length.toString(),
  top_journalist_name: mediaList[0]?.name || vars.top_journalist || "TBD",
  top_journalist_pub: mediaList[0]?.publication || vars.top_publication || "TBD",
  top_priority_score: mediaList[0]?.priority_score?.toString() || "0",
  list_summary: mediaList.slice(0, 5).map(j => "#" + j.rank + " " + j.name + " @ " + j.publication + " (" + j.priority_score + ")").join(", ")
};`,
          outputVariables: [
            { name: "media_list_json", type: "string" },
            { name: "media_list_count", type: "string" },
            { name: "top_journalist_name", type: "string" },
            { name: "top_journalist_pub", type: "string" },
            { name: "top_priority_score", type: "string" },
            { name: "list_summary", type: "string" },
          ],
        },
      }),

      // 6. AI personalized pitch generator
      aiNode(
        "6",
        "AI: Generate personalized pitch",
        1240,
        "You are a PR pitch writer. Write a concise, personalized pitch for the top journalist. Reference their recent coverage, explain why this story fits their beat, and offer an exclusive angle. Keep it under 200 words. Return JSON: {pitch_subject: string, pitch_body: string, exclusive_offer: string, assets_available: string}",
        "Journalist: {top_journalist_name} at {top_journalist_pub}. Topic: {pr_topic}. Angle: {pr_angle}. Key messages: {key_messages}. Coverage insights: {coverage_insights}. Priority score: {top_priority_score}.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body", as: "pitch_body", type: "string" },
          { field: "exclusive_offer", as: "exclusive_offer", type: "string" },
          { field: "assets_available", as: "assets_available", type: "string" },
        ],
      ),

      // 7. Pitch approval gate
      n("7", "approvalGate", "Pitch approval gate", 1480, {
        approvalGateDetails: {
          approverEmail: "{pr_manager_email}",
          approvalMessage:
            "PR Pitch Ready for Review\\n\\nTopic: {pr_topic}\\nAngle: {pr_angle}\\nTarget: {top_journalist_name} @ {top_journalist_pub}\\n\\nSubject: {pitch_subject}\\n\\nPitch:\\n{pitch_body}\\n\\nExclusive: {exclusive_offer}\\nAssets: {assets_available}\\n\\nMedia List: {media_list_count} journalists qualified\\nTop 5: {list_summary}\\n\\nApprove to finalize outreach campaign.",
        },
      }),

      // 8. Media list builder + outreach logger (Sheets)
      sheetsNode(
        "8",
        "Build media list and log outreach (Sheets)",
        1720,
        "MediaList",
        '[["{pr_topic}","{pr_angle}","{top_journalist_name}","{top_journalist_pub}","{top_priority_score}","{pitch_subject}","{media_list_count}","approved","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1081. CONTENT GAP ANALYSIS (9 nodes)
  // ===================================================================

  "Content Gap Analysis": {
    summary:
      "Identifies content gaps between your site and competitors. Accepts competitor and keyword inputs via webhook, crawls own content inventory, crawls competitor content via SerpAPI, compares SERP rankings, identifies gaps with AI, generates content briefs, prioritizes by impact in runCode, logs the gap analysis to Sheets, and distributes assignments via Slack.",
    nodes: [
      trigger("Webhook: Content gap analysis request", "webhook_app", {
        source: "SEO / Content Strategy Tool",
        event: "gap.analysis.requested",
        howItStarts:
          "A content gap analysis request is submitted via webhook with competitor domains, target keywords, and content categories to analyze for missed opportunities",
        setup:
          "Configure SerpAPI for competitor content crawling and SERP analysis. Set up Google Sheets for the gap analysis report. Add Slack for team distribution.",
        inputFields: [],
      }),

      // 2. Own content crawl (extract from payload + SerpAPI site search)
      aiNode(
        "2",
        "AI: Extract analysis parameters",
        280,
        "You are an SEO content strategist. Extract competitor domains, target keywords, content categories, and own domain from the request. Return JSON: {own_domain: string, competitors: string, target_keywords: string, categories: string, analysis_scope: string}",
        "Analysis request: {webhook_payload}. Extract all parameters needed for a comprehensive content gap analysis.",
        [
          { field: "own_domain", as: "own_domain", type: "string" },
          { field: "competitors", as: "competitor_domains", type: "string" },
          { field: "target_keywords", as: "target_keywords", type: "string" },
          { field: "categories", as: "content_categories", type: "string" },
          { field: "analysis_scope", as: "analysis_scope", type: "string" },
        ],
      ),

      // 3. Competitor content crawl (SerpAPI)
      serpApiNode(
        "3",
        "SerpAPI: Crawl competitor content coverage",
        520,
        "site:{competitor_domains} {target_keywords}",
        [
          {
            name: "organic_results",
            customName: "competitor_content_results",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "competitor_pages_found",
            type: "number",
          },
        ],
      ),

      // 4. SERP ranking comparison
      serpApiNode(
        "4",
        "SerpAPI: Compare SERP rankings",
        760,
        "{target_keywords}",
        [
          {
            name: "organic_results",
            customName: "serp_ranking_results",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "serp_total_results",
            type: "number",
          },
          {
            name: "related_searches",
            customName: "related_keywords",
            type: "string",
          },
        ],
      ),

      // 5. AI gap identification
      aiNode(
        "5",
        "AI: Identify content gaps",
        1000,
        "You are a content gap analysis expert. Compare the competitor content with SERP rankings to identify topics, keywords, and content types that competitors rank for but we do not. Return JSON: {gaps: string, total_gaps: string, high_priority_gaps: string, content_types_missing: string, keyword_clusters: string, competitive_advantage: string}",
        "Own domain: {own_domain}. Competitor content: {competitor_content_results}. SERP results for our keywords: {serp_ranking_results}. Related: {related_keywords}. Categories: {content_categories}. Identify where competitors outrank us or cover topics we miss.",
        [
          { field: "gaps", as: "identified_gaps", type: "string" },
          { field: "total_gaps", as: "total_gaps", type: "string" },
          {
            field: "high_priority_gaps",
            as: "high_priority_gaps",
            type: "string",
          },
          {
            field: "content_types_missing",
            as: "missing_content_types",
            type: "string",
          },
          {
            field: "keyword_clusters",
            as: "gap_keyword_clusters",
            type: "string",
          },
          {
            field: "competitive_advantage",
            as: "competitive_advantage",
            type: "string",
          },
        ],
      ),

      // 6. AI content brief generator
      aiNode(
        "6",
        "AI: Generate content briefs for gaps",
        1240,
        "You are a content strategist. For the top 5 content gaps identified, write a brief content brief: target keyword, suggested title, content type (blog, guide, comparison, etc.), word count recommendation, and key points to cover. Return JSON: {briefs: string, total_briefs: string, estimated_effort_hours: string, projected_traffic_impact: string}",
        "Gaps: {identified_gaps}. High priority: {high_priority_gaps}. Missing types: {missing_content_types}. Clusters: {gap_keyword_clusters}. Competitive advantage: {competitive_advantage}. Generate actionable content briefs for the top gaps.",
        [
          { field: "briefs", as: "content_briefs", type: "string" },
          { field: "total_briefs", as: "total_briefs", type: "string" },
          {
            field: "estimated_effort_hours",
            as: "effort_hours",
            type: "string",
          },
          {
            field: "projected_traffic_impact",
            as: "traffic_impact",
            type: "string",
          },
        ],
      ),

      // 7. Priority ranker (runCode)
      n("7", "runCode", "Prioritize gaps by impact and effort", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Rank content gaps by potential impact vs. effort
const gaps = JSON.parse(vars.identified_gaps || '[]');
const totalGaps = parseInt(vars.total_gaps || "0");
const highPriority = vars.high_priority_gaps || "";
const effortHours = parseFloat(vars.effort_hours || "0");
const trafficImpact = vars.traffic_impact || "unknown";
// Score priorities
const prioritized = (Array.isArray(gaps) ? gaps : []).map((g, idx) => {
  const isPriority = highPriority.toLowerCase().includes((g.keyword || g.topic || "").toLowerCase());
  const score = isPriority ? 90 - idx : 60 - idx;
  return { ...g, priority_score: Math.max(0, score), priority_rank: 0 };
});
prioritized.sort((a, b) => b.priority_score - a.priority_score);
prioritized.forEach((g, i) => g.priority_rank = i + 1);
return {
  prioritized_gaps_json: JSON.stringify(prioritized.slice(0, 10)),
  actionable_gaps: Math.min(prioritized.length, 10).toString(),
  top_gap: prioritized[0]?.keyword || prioritized[0]?.topic || "N/A",
  total_effort_hours: effortHours.toString(),
  priority_summary: prioritized.slice(0, 5).map(g => "#" + g.priority_rank + ": " + (g.keyword || g.topic || "Unknown")).join(", "),
  implementation_plan: totalGaps + " gaps found, " + Math.min(prioritized.length, 10) + " actionable, ~" + effortHours + "h estimated effort"
};`,
          outputVariables: [
            { name: "prioritized_gaps_json", type: "string" },
            { name: "actionable_gaps", type: "string" },
            { name: "top_gap", type: "string" },
            { name: "total_effort_hours", type: "string" },
            { name: "priority_summary", type: "string" },
            { name: "implementation_plan", type: "string" },
          ],
        },
      }),

      // 8. Gap analysis report (Sheets)
      sheetsNode(
        "8",
        "Log gap analysis report to Sheets",
        1720,
        "ContentGaps",
        '[["{own_domain}","{competitor_domains}","{total_gaps}","{actionable_gaps}","{top_gap}","{missing_content_types}","{total_effort_hours}h","{traffic_impact}","{priority_summary}","=NOW()"]]',
      ),

      // 9. Team distribution (Slack)
      slackNode(
        "9",
        "Distribute gap analysis to team (Slack)",
        1960,
        "CONTENT GAP ANALYSIS COMPLETE\\n\\nDomain: {own_domain} vs. {competitor_domains}\\nTotal Gaps: {total_gaps}\\nActionable: {actionable_gaps}\\n\\nTop Priority: {top_gap}\\nMissing Types: {missing_content_types}\\n\\nPriority List: {priority_summary}\\n\\nImplementation: {implementation_plan}\\nTraffic Impact: {traffic_impact}\\n\\nBriefs and full report in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1082. SERP HEADING EXTRACTOR (7 nodes)
  // ===================================================================

  "SERP Heading Extractor": {
    summary:
      "Extracts and analyzes heading patterns from top-ranking SERP results to build optimal content outlines. Accepts a target keyword via webhook, fetches SERP results via SerpAPI, scrapes page content, analyzes heading patterns in runCode, generates an optimal outline with AI, builds the outline report in Google Sheets, and delivers to the content team via Slack.",
    nodes: [
      trigger("Webhook: Target keyword for heading extraction", "webhook_app", {
        source: "SEO / Content Tool",
        event: "keyword.heading.analysis",
        howItStarts:
          "A target keyword is submitted via webhook to extract heading structures from the top-ranking SERP results and generate an optimized content outline",
        setup:
          "Configure SerpAPI for SERP fetching. Set up Google Sheets for outline reports. Add Slack webhook for content team delivery.",
        inputFields: [],
      }),

      // 2. SERP results fetch (SerpAPI)
      aiNode(
        "2",
        "AI: Extract keyword and search parameters",
        280,
        "You are an SEO analyst. Extract the target keyword and any modifiers from the payload. Return JSON: {target_keyword: string, search_intent: string, content_format: string}",
        "Heading extraction request: {webhook_payload}. Extract the keyword and infer search intent.",
        [
          { field: "target_keyword", as: "target_keyword", type: "string" },
          { field: "search_intent", as: "search_intent", type: "string" },
          { field: "content_format", as: "content_format", type: "string" },
        ],
      ),

      // 3. SerpAPI fetch
      serpApiNode(
        "3",
        "SerpAPI: Fetch top SERP results",
        520,
        "{target_keyword}",
        [
          {
            name: "organic_results",
            customName: "serp_results",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "results_count",
            type: "number",
          },
          {
            name: "search_metadata.total_time_taken",
            customName: "search_time",
            type: "string",
          },
        ],
      ),

      // 4. Heading pattern analyzer (runCode)
      n("4", "runCode", "Analyze heading patterns from SERP results", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and analyze heading patterns from SERP result snippets and titles
const results = JSON.parse(vars.serp_results || '[]');
const keyword = vars.target_keyword || "";
const headingPatterns = [];
const commonH2Topics = {};
const titlePatterns = [];
for (const r of results.slice(0, 10)) {
  const title = r.title || "";
  const snippet = r.snippet || "";
  titlePatterns.push(title);
  // Extract implied headings from snippets (sentences that look like section headers)
  const sentences = snippet.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 10 && s.length < 80);
  for (const s of sentences) {
    headingPatterns.push(s);
    const words = s.toLowerCase().split(/\\s+/).filter(w => w.length > 3);
    for (const w of words) {
      commonH2Topics[w] = (commonH2Topics[w] || 0) + 1;
    }
  }
}
const topTopics = Object.entries(commonH2Topics)
  .filter(([w]) => !keyword.toLowerCase().includes(w))
  .sort((a, b) => b[1] - a[1])
  .slice(0, 15)
  .map(([w, c]) => w + " (" + c + ")");
return {
  heading_patterns_json: JSON.stringify(headingPatterns.slice(0, 30)),
  title_patterns_json: JSON.stringify(titlePatterns),
  common_topics: topTopics.join(", "),
  total_patterns_extracted: headingPatterns.length.toString(),
  pages_analyzed: Math.min(results.length, 10).toString(),
  avg_title_length: titlePatterns.length > 0 ? Math.round(titlePatterns.reduce((s, t) => s + t.length, 0) / titlePatterns.length).toString() : "0"
};`,
          outputVariables: [
            { name: "heading_patterns_json", type: "string" },
            { name: "title_patterns_json", type: "string" },
            { name: "common_topics", type: "string" },
            { name: "total_patterns_extracted", type: "string" },
            { name: "pages_analyzed", type: "string" },
            { name: "avg_title_length", type: "string" },
          ],
        },
      }),

      // 5. AI optimal outline generator
      aiNode(
        "5",
        "AI: Generate optimal content outline",
        1000,
        "You are an SEO content architect. Based on the heading patterns extracted from top-ranking pages, create an optimal content outline that covers all important subtopics while offering a unique angle. Include H1, H2s, and H3s. Return JSON: {recommended_title: string, h1: string, outline: string, unique_angle: string, word_count_recommendation: string, seo_notes: string}",
        "Keyword: {target_keyword}. Intent: {search_intent}. Format: {content_format}. Pages analyzed: {pages_analyzed}. Common topics: {common_topics}. Heading patterns: {heading_patterns_json}. Title patterns: {title_patterns_json}. Create an outline that comprehensively covers the topic.",
        [
          {
            field: "recommended_title",
            as: "recommended_title",
            type: "string",
          },
          { field: "h1", as: "h1_tag", type: "string" },
          { field: "outline", as: "content_outline", type: "string" },
          { field: "unique_angle", as: "unique_angle", type: "string" },
          {
            field: "word_count_recommendation",
            as: "word_count_rec",
            type: "string",
          },
          { field: "seo_notes", as: "seo_notes", type: "string" },
        ],
      ),

      // 6. Outline report builder (Sheets)
      sheetsNode(
        "6",
        "Build outline report (Sheets)",
        1240,
        "ContentOutlines",
        '[["{target_keyword}","{search_intent}","{recommended_title}","{h1_tag}","{pages_analyzed}","{common_topics}","{word_count_rec}","{unique_angle}","=NOW()"]]',
      ),

      // 7. Content team delivery (Slack)
      slackNode(
        "7",
        "Deliver outline to content team (Slack)",
        1480,
        "CONTENT OUTLINE READY\\n\\nKeyword: {target_keyword}\\nIntent: {search_intent}\\n\\nRecommended Title: {recommended_title}\\nH1: {h1_tag}\\nWord Count: {word_count_rec}\\n\\nUnique Angle: {unique_angle}\\nCommon Topics: {common_topics}\\n\\nSEO Notes: {seo_notes}\\nPages Analyzed: {pages_analyzed}\\n\\nFull outline in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1083. PAA MINER (8 nodes)
  // ===================================================================

  "PAA Miner": {
    summary:
      "Mines Google People Also Ask questions for content ideation and FAQ generation. Accepts a seed keyword via webhook, extracts PAA questions via SerpAPI, deduplicates and cleans questions in runCode, clusters questions by theme with AI, enriches with search volume data, generates AI FAQ content for each cluster, builds a FAQ output in Sheets, and integrates with the content calendar via Slack.",
    nodes: [
      trigger("Webhook: PAA mining seed keyword", "webhook_app", {
        source: "SEO / Content Tool",
        event: "keyword.paa.mining",
        howItStarts:
          "A seed keyword is submitted via webhook to mine People Also Ask questions from Google SERPs for content ideation and FAQ page generation",
        setup:
          "Configure SerpAPI for PAA extraction. Set up Google Sheets for FAQ outputs. Add Slack for content calendar integration.",
        inputFields: [],
      }),

      // 2. PAA extraction (SerpAPI)
      aiNode(
        "2",
        "AI: Extract seed keyword",
        280,
        "You are an SEO keyword researcher. Extract the seed keyword and related context from the payload. Return JSON: {seed_keyword: string, topic_area: string, target_audience: string}",
        "PAA mining request: {webhook_payload}. Extract the seed keyword and context.",
        [
          { field: "seed_keyword", as: "seed_keyword", type: "string" },
          { field: "topic_area", as: "topic_area", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
        ],
      ),

      // 3. SerpAPI PAA fetch
      serpApiNode(
        "3",
        "SerpAPI: Extract PAA questions",
        520,
        "{seed_keyword}",
        [
          {
            name: "related_questions",
            customName: "paa_questions_raw",
            type: "string",
          },
          {
            name: "organic_results",
            customName: "serp_results",
            type: "string",
          },
          {
            name: "search_information.total_results",
            customName: "total_search_results",
            type: "string",
          },
        ],
      ),

      // 4. Question deduplicator (runCode)
      n("4", "runCode", "Deduplicate and clean PAA questions", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse, deduplicate, and clean PAA questions
const raw = JSON.parse(vars.paa_questions_raw || '[]');
const questions = [];
const seen = new Set();
for (const q of raw) {
  const text = (q.question || q.title || "").trim();
  const normalized = text.toLowerCase().replace(/[^a-z0-9\\s]/g, "").replace(/\\s+/g, " ");
  if (normalized.length > 10 && !seen.has(normalized)) {
    seen.add(normalized);
    questions.push({
      question: text,
      snippet: q.snippet || q.answer || "",
      source: q.link || q.source || ""
    });
  }
}
const keyword = vars.seed_keyword || "";
// Categorize by question type
const howQuestions = questions.filter(q => q.question.toLowerCase().startsWith("how"));
const whatQuestions = questions.filter(q => q.question.toLowerCase().startsWith("what"));
const whyQuestions = questions.filter(q => q.question.toLowerCase().startsWith("why"));
const otherQuestions = questions.filter(q =>
  !q.question.toLowerCase().startsWith("how") &&
  !q.question.toLowerCase().startsWith("what") &&
  !q.question.toLowerCase().startsWith("why")
);
return {
  clean_questions_json: JSON.stringify(questions),
  total_unique_questions: questions.length.toString(),
  how_count: howQuestions.length.toString(),
  what_count: whatQuestions.length.toString(),
  why_count: whyQuestions.length.toString(),
  other_count: otherQuestions.length.toString(),
  question_preview: questions.slice(0, 5).map(q => q.question).join(" | ")
};`,
          outputVariables: [
            { name: "clean_questions_json", type: "string" },
            { name: "total_unique_questions", type: "string" },
            { name: "how_count", type: "string" },
            { name: "what_count", type: "string" },
            { name: "why_count", type: "string" },
            { name: "other_count", type: "string" },
            { name: "question_preview", type: "string" },
          ],
        },
      }),

      // 5. AI theme clusterer
      aiNode(
        "5",
        "AI: Cluster questions by theme",
        1000,
        "You are a content strategist specializing in information architecture. Cluster the PAA questions into thematic groups. Each cluster should represent a potential FAQ section or blog post topic. Return JSON: {clusters: string, total_clusters: string, primary_cluster: string, cluster_summary: string, content_opportunities: string}",
        "Seed keyword: {seed_keyword}. Topic: {topic_area}. Questions ({total_unique_questions} unique): {clean_questions_json}. How: {how_count}, What: {what_count}, Why: {why_count}, Other: {other_count}. Group into thematic clusters.",
        [
          { field: "clusters", as: "question_clusters", type: "string" },
          { field: "total_clusters", as: "total_clusters", type: "string" },
          { field: "primary_cluster", as: "primary_cluster", type: "string" },
          { field: "cluster_summary", as: "cluster_summary", type: "string" },
          {
            field: "content_opportunities",
            as: "content_opportunities",
            type: "string",
          },
        ],
      ),

      // 6. AI FAQ content generator
      aiNode(
        "6",
        "AI: Generate FAQ content for clusters",
        1240,
        "You are a content writer who creates comprehensive, helpful FAQ answers. For each question cluster, write clear, authoritative FAQ answers (150-250 words each) that would rank well in search. Return JSON: {faq_content: string, total_faqs_written: string, schema_markup_ready: string, estimated_word_count: string}",
        "Clusters: {question_clusters}. Keyword: {seed_keyword}. Audience: {target_audience}. Write FAQ answers that are comprehensive but scannable. Include schema markup indicators.",
        [
          { field: "faq_content", as: "faq_content", type: "string" },
          {
            field: "total_faqs_written",
            as: "total_faqs_written",
            type: "string",
          },
          { field: "schema_markup_ready", as: "schema_ready", type: "string" },
          {
            field: "estimated_word_count",
            as: "faq_word_count",
            type: "string",
          },
        ],
      ),

      // 7. FAQ output builder (Sheets)
      sheetsNode(
        "7",
        "Build FAQ output (Sheets)",
        1480,
        "PAA_FAQs",
        '[["{seed_keyword}","{topic_area}","{total_unique_questions}","{total_clusters}","{primary_cluster}","{total_faqs_written}","{faq_word_count}","{schema_ready}","{question_preview}","=NOW()"]]',
      ),

      // 8. Content calendar integration (Slack)
      slackNode(
        "8",
        "Integrate with content calendar (Slack)",
        1720,
        "PAA MINING COMPLETE\\n\\nKeyword: {seed_keyword}\\nUnique Questions: {total_unique_questions}\\nClusters: {total_clusters}\\nFAQs Written: {total_faqs_written}\\n\\nPrimary Cluster: {primary_cluster}\\nQuestions: {question_preview}\\n\\nContent Opportunities: {content_opportunities}\\nWord Count: {faq_word_count}\\nSchema Ready: {schema_ready}\\n\\nFull FAQ content in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1084. SEARCH INTENT CLASSIFIER (7 nodes)
  // ===================================================================

  "Search Intent Classifier": {
    summary:
      "Classifies the search intent behind a batch of keywords. Accepts a keyword batch via webhook, analyzes SERP features via SerpAPI, classifies intent with AI, scores classification confidence in runCode, maps content strategy recommendations, builds a classification report in Sheets, and distributes to the team via Slack.",
    nodes: [
      trigger(
        "Webhook: Keyword batch for intent classification",
        "webhook_app",
        {
          source: "SEO / Content Tool",
          event: "keyword.intent.classify",
          howItStarts:
            "A batch of keywords is submitted via webhook for search intent classification, triggering SERP analysis and AI-powered intent mapping",
          setup:
            "Configure SerpAPI for SERP feature analysis. Set up Google Sheets for classification reports. Add Slack for team distribution.",
          inputFields: [],
        },
      ),

      // 2. SERP feature analyzer (SerpAPI)
      aiNode(
        "2",
        "AI: Extract keyword batch",
        280,
        "You are a keyword analyst. Extract the list of keywords from the payload. Return JSON: {keywords: string, total_keywords: string, primary_keyword: string, category: string}",
        "Keyword batch: {webhook_payload}. Extract all keywords as a comma-separated list.",
        [
          { field: "keywords", as: "keyword_list", type: "string" },
          { field: "total_keywords", as: "total_keywords", type: "string" },
          { field: "primary_keyword", as: "primary_keyword", type: "string" },
          { field: "category", as: "keyword_category", type: "string" },
        ],
      ),

      // 3. SerpAPI SERP feature fetch
      serpApiNode(
        "3",
        "SerpAPI: Analyze SERP features",
        520,
        "{primary_keyword}",
        [
          {
            name: "organic_results",
            customName: "serp_organic",
            type: "string",
          },
          {
            name: "knowledge_graph",
            customName: "serp_knowledge_graph",
            type: "string",
          },
          { name: "related_questions", customName: "serp_paa", type: "string" },
          {
            name: "shopping_results",
            customName: "serp_shopping",
            type: "string",
          },
          { name: "local_results", customName: "serp_local", type: "string" },
        ],
      ),

      // 4. AI intent classifier
      aiNode(
        "4",
        "AI: Classify search intent for keywords",
        760,
        "You are a search intent classification expert. Analyze the SERP features and results to classify the intent of each keyword. Categories: informational (how-to, what-is, guides), navigational (brand/site-specific), commercial (comparison, best, review), transactional (buy, pricing, discount). Return JSON: {classifications: string, informational_count: string, navigational_count: string, commercial_count: string, transactional_count: string, mixed_intent_keywords: string}",
        "Keywords: {keyword_list}. SERP organic: {serp_organic}. Knowledge graph: {serp_knowledge_graph}. PAA: {serp_paa}. Shopping: {serp_shopping}. Local: {serp_local}. Classify the intent of each keyword based on SERP features.",
        [
          {
            field: "classifications",
            as: "intent_classifications",
            type: "string",
          },
          {
            field: "informational_count",
            as: "informational_count",
            type: "string",
          },
          {
            field: "navigational_count",
            as: "navigational_count",
            type: "string",
          },
          { field: "commercial_count", as: "commercial_count", type: "string" },
          {
            field: "transactional_count",
            as: "transactional_count",
            type: "string",
          },
          {
            field: "mixed_intent_keywords",
            as: "mixed_intent_kws",
            type: "string",
          },
        ],
      ),

      // 5. Confidence scorer (runCode)
      n(
        "5",
        "runCode",
        "Score classification confidence and map strategy",
        1000,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Score confidence of intent classifications and map content strategy
const classifications = JSON.parse(vars.intent_classifications || '[]');
const total = parseInt(vars.total_keywords || "0");
const infoCount = parseInt(vars.informational_count || "0");
const navCount = parseInt(vars.navigational_count || "0");
const comCount = parseInt(vars.commercial_count || "0");
const txnCount = parseInt(vars.transactional_count || "0");
// Map intent to content type
const strategyMap = {
  informational: "Blog posts, guides, tutorials, FAQ pages",
  navigational: "Landing pages, branded content, feature pages",
  commercial: "Comparison pages, reviews, buyer guides, case studies",
  transactional: "Product pages, pricing pages, sign-up flows, demo CTAs"
};
const dominantIntent = [
  { type: "informational", count: infoCount },
  { type: "navigational", count: navCount },
  { type: "commercial", count: comCount },
  { type: "transactional", count: txnCount }
].sort((a, b) => b.count - a.count)[0];
const confidenceScore = total > 0 ? Math.round((dominantIntent.count / total) * 100) : 0;
return {
  dominant_intent: dominantIntent.type,
  dominant_intent_pct: confidenceScore.toString(),
  confidence_level: confidenceScore > 70 ? "high" : confidenceScore > 40 ? "medium" : "low",
  content_strategy: strategyMap[dominantIntent.type] || "Mixed content types",
  intent_distribution: "Info: " + infoCount + " | Nav: " + navCount + " | Com: " + comCount + " | Txn: " + txnCount,
  strategy_summary: dominantIntent.type + " dominant (" + confidenceScore + "%). Recommended: " + (strategyMap[dominantIntent.type] || "varied content")
};`,
            outputVariables: [
              { name: "dominant_intent", type: "string" },
              { name: "dominant_intent_pct", type: "string" },
              { name: "confidence_level", type: "string" },
              { name: "content_strategy", type: "string" },
              { name: "intent_distribution", type: "string" },
              { name: "strategy_summary", type: "string" },
            ],
          },
        },
      ),

      // 6. Classification report (Sheets)
      sheetsNode(
        "6",
        "Build classification report (Sheets)",
        1240,
        "IntentClassification",
        '[["{primary_keyword}","{keyword_category}","{total_keywords}","{dominant_intent}","{dominant_intent_pct}%","{confidence_level}","{intent_distribution}","{content_strategy}","{mixed_intent_kws}","=NOW()"]]',
      ),

      // 7. Team distribution (Slack)
      slackNode(
        "7",
        "Distribute classification report (Slack)",
        1480,
        "SEARCH INTENT CLASSIFICATION COMPLETE\\n\\nKeywords: {total_keywords} analyzed\\nCategory: {keyword_category}\\n\\nDominant Intent: {dominant_intent} ({dominant_intent_pct}%)\\nConfidence: {confidence_level}\\nDistribution: {intent_distribution}\\n\\nStrategy: {strategy_summary}\\nContent Types: {content_strategy}\\nMixed Intent: {mixed_intent_kws}\\n\\nFull report in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1085. LONG-TAIL EXPANSION (8 nodes)
  // ===================================================================

  "Long-Tail Expansion": {
    summary:
      "Expands seed keywords into long-tail variations for content targeting. Accepts a seed keyword via webhook, harvests Google Suggest via SerpAPI, extracts PAA questions, uses AI for semantic expansion, deduplicates and clusters in runCode, enriches with difficulty signals, ranks by priority, and delivers the keyword report via Sheets.",
    nodes: [
      trigger("Webhook: Seed keyword for long-tail expansion", "webhook_app", {
        source: "SEO / Content Tool",
        event: "keyword.longtail.expand",
        howItStarts:
          "A seed keyword is submitted via webhook to generate long-tail keyword variations using Google Suggest, PAA mining, and AI semantic expansion",
        setup:
          "Configure SerpAPI for Google Suggest harvesting and PAA extraction. Set up Google Sheets for keyword reports.",
        inputFields: [],
      }),

      // 2. Google Suggest harvester (SerpAPI)
      aiNode(
        "2",
        "AI: Extract seed keyword and context",
        280,
        "You are a keyword research specialist. Extract the seed keyword, target market, and any context from the payload. Return JSON: {seed_keyword: string, market: string, language: string, context: string}",
        "Long-tail expansion request: {webhook_payload}. Extract the seed keyword and context.",
        [
          { field: "seed_keyword", as: "seed_keyword", type: "string" },
          { field: "market", as: "target_market", type: "string" },
          { field: "language", as: "language", type: "string" },
          { field: "context", as: "expansion_context", type: "string" },
        ],
      ),

      // 3. SerpAPI Google Suggest + PAA
      serpApiNode(
        "3",
        "SerpAPI: Harvest suggestions and PAA",
        520,
        "{seed_keyword}",
        [
          {
            name: "related_searches",
            customName: "related_searches",
            type: "string",
          },
          {
            name: "related_questions",
            customName: "paa_questions",
            type: "string",
          },
          {
            name: "organic_results",
            customName: "organic_results",
            type: "string",
          },
          {
            name: "search_information.total_results",
            customName: "total_results_count",
            type: "string",
          },
        ],
      ),

      // 4. AI semantic expander
      aiNode(
        "4",
        "AI: Semantically expand keyword variations",
        760,
        "You are a long-tail keyword research expert. Using the seed keyword and Google data, generate additional long-tail variations by applying modifiers (question words, comparison terms, location qualifiers, intent modifiers, year modifiers). Return JSON: {expanded_keywords: string, total_generated: string, question_variations: string, comparison_variations: string, modifier_categories: string}",
        "Seed: {seed_keyword}. Market: {target_market}. Related searches: {related_searches}. PAA: {paa_questions}. Context: {expansion_context}. Generate as many relevant long-tail variations as possible.",
        [
          {
            field: "expanded_keywords",
            as: "expanded_keywords",
            type: "string",
          },
          {
            field: "total_generated",
            as: "ai_generated_count",
            type: "string",
          },
          {
            field: "question_variations",
            as: "question_variations",
            type: "string",
          },
          {
            field: "comparison_variations",
            as: "comparison_variations",
            type: "string",
          },
          {
            field: "modifier_categories",
            as: "modifier_categories",
            type: "string",
          },
        ],
      ),

      // 5. Dedup + clustering (runCode)
      n("5", "runCode", "Deduplicate and cluster long-tail keywords", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Merge all keyword sources, deduplicate, and cluster by semantic similarity
const expanded = JSON.parse(vars.expanded_keywords || '[]');
const related = JSON.parse(vars.related_searches || '[]');
const paa = JSON.parse(vars.paa_questions || '[]');
const allKeywords = new Set();
// Add AI-generated keywords
(Array.isArray(expanded) ? expanded : [expanded]).forEach(k => {
  const kw = typeof k === "string" ? k : k.keyword || k.text || "";
  if (kw.trim()) allKeywords.add(kw.trim().toLowerCase());
});
// Add related searches
(Array.isArray(related) ? related : []).forEach(r => {
  const q = r.query || r.text || (typeof r === "string" ? r : "");
  if (q.trim()) allKeywords.add(q.trim().toLowerCase());
});
// Add PAA as question keywords
(Array.isArray(paa) ? paa : []).forEach(p => {
  const q = p.question || p.title || (typeof p === "string" ? p : "");
  if (q.trim()) allKeywords.add(q.trim().toLowerCase());
});
const unique = Array.from(allKeywords);
// Simple clustering by word overlap
const clusters = {};
for (const kw of unique) {
  const words = kw.split(/\\s+/).filter(w => w.length > 3);
  const clusterKey = words.slice(0, 2).join("_") || "misc";
  if (!clusters[clusterKey]) clusters[clusterKey] = [];
  clusters[clusterKey].push(kw);
}
const clusterCount = Object.keys(clusters).length;
return {
  unique_keywords_json: JSON.stringify(unique),
  total_unique: unique.length.toString(),
  cluster_count: clusterCount.toString(),
  clusters_json: JSON.stringify(clusters),
  keyword_preview: unique.slice(0, 10).join(" | "),
  source_breakdown: "AI: " + vars.ai_generated_count + " | Related: " + related.length + " | PAA: " + paa.length
};`,
          outputVariables: [
            { name: "unique_keywords_json", type: "string" },
            { name: "total_unique", type: "string" },
            { name: "cluster_count", type: "string" },
            { name: "clusters_json", type: "string" },
            { name: "keyword_preview", type: "string" },
            { name: "source_breakdown", type: "string" },
          ],
        },
      }),

      // 6. Volume + difficulty enricher
      n("6", "runCode", "Estimate difficulty and prioritize keywords", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Estimate keyword difficulty and prioritize based on length and competition signals
const keywords = JSON.parse(vars.unique_keywords_json || '[]');
const totalSearchResults = parseInt(vars.total_results_count || "0");
const prioritized = keywords.map(kw => {
  const wordCount = kw.split(/\\s+/).length;
  // Longer tails = lower competition estimate
  const estDifficulty = Math.max(5, 80 - (wordCount - 2) * 15);
  // Question keywords often have lower competition
  const isQuestion = kw.startsWith("how") || kw.startsWith("what") || kw.startsWith("why") || kw.startsWith("when");
  const adjustedDifficulty = isQuestion ? Math.max(5, estDifficulty - 10) : estDifficulty;
  const priority = Math.round(100 - adjustedDifficulty + (wordCount * 5));
  return {
    keyword: kw,
    word_count: wordCount,
    est_difficulty: adjustedDifficulty,
    is_question: isQuestion,
    priority_score: Math.min(100, Math.max(0, priority))
  };
});
prioritized.sort((a, b) => b.priority_score - a.priority_score);
return {
  prioritized_keywords_json: JSON.stringify(prioritized.slice(0, 50)),
  total_prioritized: Math.min(prioritized.length, 50).toString(),
  top_keyword: prioritized[0]?.keyword || "N/A",
  top_priority_score: prioritized[0]?.priority_score?.toString() || "0",
  avg_difficulty: prioritized.length > 0 ? Math.round(prioritized.reduce((s, k) => s + k.est_difficulty, 0) / prioritized.length).toString() : "0",
  easy_keywords_count: prioritized.filter(k => k.est_difficulty < 30).length.toString()
};`,
          outputVariables: [
            { name: "prioritized_keywords_json", type: "string" },
            { name: "total_prioritized", type: "string" },
            { name: "top_keyword", type: "string" },
            { name: "top_priority_score", type: "string" },
            { name: "avg_difficulty", type: "string" },
            { name: "easy_keywords_count", type: "string" },
          ],
        },
      }),

      // 7. Priority ranker (already done in node 6, this node serves as Sheets output)
      sheetsNode(
        "7",
        "Build keyword expansion report (Sheets)",
        1480,
        "LongTailKeywords",
        '[["{seed_keyword}","{target_market}","{total_unique}","{cluster_count}","{top_keyword}","{top_priority_score}","{avg_difficulty}","{easy_keywords_count}","{source_breakdown}","{keyword_preview}","=NOW()"]]',
      ),

      // 8. Keyword report delivery (Sheets link via Slack)
      slackNode(
        "8",
        "Deliver keyword report via Slack",
        1720,
        "LONG-TAIL EXPANSION COMPLETE\\n\\nSeed: {seed_keyword}\\nMarket: {target_market}\\n\\nTotal Unique: {total_unique}\\nClusters: {cluster_count}\\nSource: {source_breakdown}\\n\\nTop Keyword: {top_keyword} (Priority: {top_priority_score})\\nAvg Difficulty: {avg_difficulty}\\nEasy Keywords: {easy_keywords_count}\\n\\nPreview: {keyword_preview}\\n\\nFull keyword report in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1086. COMPETITOR SITEMAP DIFF (8 nodes)
  // ===================================================================

  "Competitor Sitemap Diff": {
    summary:
      "Monitors competitor sitemaps for new and removed pages. Fetches the competitor sitemap via HTTP, parses URLs in runCode, compares against historical data, categorizes new URLs with AI, scores competitive intelligence value, archives the latest sitemap in Sheets, builds an intelligence report, and alerts the team via Slack.",
    nodes: [
      trigger("Schedule: Competitor sitemap monitoring", "schedule", {
        schedule: "0 5 * * 1",
        howItStarts:
          "Runs weekly on Monday at 5 AM to fetch competitor sitemaps, compare against previous snapshots, and identify new or removed pages for competitive intelligence",
        setup:
          "Configure competitor sitemap URLs. Set up Google Sheets for sitemap archive and intelligence reports. Add Slack webhook for team alerts.",
        inputFields: [],
      }),

      // 2. Competitor sitemap fetch
      n("2", "hitApi", "Fetch competitor sitemap XML", 280, {
        hitApiDetails: {
          method: "GET",
          url: "{competitor_sitemap_url}",
          headers: [
            {
              key: "User-Agent",
              value: "Mozilla/5.0 (compatible; SitemapBot/1.0)",
            },
            { key: "Accept", value: "application/xml,text/xml" },
          ],
          body: [],
          variables: [
            { name: "body", customName: "sitemap_xml_raw", type: "string" },
            { name: "statusCode", customName: "fetch_status", type: "number" },
          ],
        },
      }),

      // 3. Sitemap parser (runCode)
      n("3", "runCode", "Parse sitemap and extract URLs", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse sitemap XML and extract URLs with metadata
const xml = vars.sitemap_xml_raw || "";
const urlPattern = /<loc>(.*?)<\\/loc>/g;
const lastmodPattern = /<lastmod>(.*?)<\\/lastmod>/g;
const urls = [];
let match;
while ((match = urlPattern.exec(xml)) !== null) {
  urls.push(match[1].trim());
}
const lastmods = [];
while ((match = lastmodPattern.exec(xml)) !== null) {
  lastmods.push(match[1].trim());
}
// Compare with previous snapshot
const previousUrls = JSON.parse(vars.previous_sitemap_urls || '[]');
const previousSet = new Set(previousUrls);
const currentSet = new Set(urls);
const newUrls = urls.filter(u => !previousSet.has(u));
const removedUrls = previousUrls.filter(u => !currentSet.has(u));
// Categorize by URL path
const blogUrls = newUrls.filter(u => u.includes("/blog") || u.includes("/article") || u.includes("/post"));
const productUrls = newUrls.filter(u => u.includes("/product") || u.includes("/feature") || u.includes("/solution"));
const otherUrls = newUrls.filter(u => !u.includes("/blog") && !u.includes("/article") && !u.includes("/product") && !u.includes("/feature"));
return {
  total_urls: urls.length.toString(),
  new_urls_json: JSON.stringify(newUrls),
  removed_urls_json: JSON.stringify(removedUrls),
  new_count: newUrls.length.toString(),
  removed_count: removedUrls.length.toString(),
  new_blog_count: blogUrls.length.toString(),
  new_product_count: productUrls.length.toString(),
  new_other_count: otherUrls.length.toString(),
  current_urls_json: JSON.stringify(urls),
  has_changes: (newUrls.length > 0 || removedUrls.length > 0) ? "yes" : "no",
  change_summary: newUrls.length + " new, " + removedUrls.length + " removed out of " + urls.length + " total"
};`,
          outputVariables: [
            { name: "total_urls", type: "string" },
            { name: "new_urls_json", type: "string" },
            { name: "removed_urls_json", type: "string" },
            { name: "new_count", type: "string" },
            { name: "removed_count", type: "string" },
            { name: "new_blog_count", type: "string" },
            { name: "new_product_count", type: "string" },
            { name: "new_other_count", type: "string" },
            { name: "current_urls_json", type: "string" },
            { name: "has_changes", type: "string" },
            { name: "change_summary", type: "string" },
          ],
        },
      }),

      // 4. URL categorizer (AI)
      aiNode(
        "4",
        "AI: Categorize and analyze new URLs",
        760,
        "You are a competitive intelligence analyst. Analyze the new and removed URLs from a competitor sitemap. Categorize each new URL by content type (blog, product, landing page, documentation, case study, etc.) and assess the strategic significance. Return JSON: {url_analysis: string, strategic_significance: string, content_focus_shift: string, notable_launches: string, competitive_threat_level: string}",
        "New URLs ({new_count}): {new_urls_json}. Removed URLs ({removed_count}): {removed_urls_json}. New blogs: {new_blog_count}. New product pages: {new_product_count}. Other: {new_other_count}. What strategic moves is the competitor making?",
        [
          { field: "url_analysis", as: "url_analysis", type: "string" },
          {
            field: "strategic_significance",
            as: "strategic_significance",
            type: "string",
          },
          {
            field: "content_focus_shift",
            as: "content_focus_shift",
            type: "string",
          },
          { field: "notable_launches", as: "notable_launches", type: "string" },
          {
            field: "competitive_threat_level",
            as: "threat_level",
            type: "string",
          },
        ],
      ),

      // 5. Competitive intelligence scorer
      n("5", "runCode", "Score competitive intelligence value", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the intelligence value of sitemap changes
const newCount = parseInt(vars.new_count || "0");
const removedCount = parseInt(vars.removed_count || "0");
const productCount = parseInt(vars.new_product_count || "0");
const threatLevel = (vars.threat_level || "").toLowerCase();
let intelScore = 0;
// More changes = more intel value
intelScore += Math.min(40, newCount * 4);
intelScore += Math.min(20, removedCount * 3);
// Product pages are high-value intel
intelScore += productCount * 10;
// Threat level bonus
if (threatLevel.includes("high")) intelScore += 20;
else if (threatLevel.includes("medium")) intelScore += 10;
intelScore = Math.min(100, intelScore);
const urgency = intelScore > 70 ? "high" : intelScore > 40 ? "medium" : "low";
return {
  intel_score: intelScore.toString(),
  urgency: urgency,
  action_items: productCount > 0 ? "Review competitor product launches immediately" : newCount > 5 ? "Content strategy shift detected — review and adapt" : "Routine monitoring — no urgent action",
  intel_summary: "Score: " + intelScore + "/100. " + newCount + " new pages, " + removedCount + " removed. Threat: " + vars.threat_level + ". Urgency: " + urgency
};`,
          outputVariables: [
            { name: "intel_score", type: "string" },
            { name: "urgency", type: "string" },
            { name: "action_items", type: "string" },
            { name: "intel_summary", type: "string" },
          ],
        },
      }),

      // 6. Sitemap archive updater (Sheets)
      sheetsNode(
        "6",
        "Archive sitemap snapshot (Sheets)",
        1240,
        "SitemapArchive",
        '[["{competitor_sitemap_url}","{total_urls}","{new_count}","{removed_count}","{new_blog_count}","{new_product_count}","{intel_score}","{threat_level}","{change_summary}","=NOW()"]]',
      ),

      // 7. Intelligence report builder
      aiNode(
        "7",
        "AI: Build competitive intelligence report",
        1480,
        "You are a competitive intelligence analyst. Compile a brief executive intelligence report summarizing the competitor sitemap changes, their strategic implications, and recommended responses. Return JSON: {executive_brief: string, strategic_implications: string, recommended_responses: string, monitoring_adjustments: string}",
        "Changes: {change_summary}. New URLs analysis: {url_analysis}. Strategic significance: {strategic_significance}. Content shift: {content_focus_shift}. Notable launches: {notable_launches}. Threat: {threat_level}. Intel score: {intel_score}. Action items: {action_items}.",
        [
          { field: "executive_brief", as: "executive_brief", type: "string" },
          {
            field: "strategic_implications",
            as: "strategic_implications",
            type: "string",
          },
          {
            field: "recommended_responses",
            as: "recommended_responses",
            type: "string",
          },
          {
            field: "monitoring_adjustments",
            as: "monitoring_adjustments",
            type: "string",
          },
        ],
      ),

      // 8. Team Slack alert
      slackNode(
        "8",
        "Alert team of sitemap changes (Slack)",
        1720,
        "COMPETITOR SITEMAP INTELLIGENCE\\n\\nChanges: {change_summary}\\nIntel Score: {intel_score}/100\\nUrgency: {urgency}\\nThreat Level: {threat_level}\\n\\n{executive_brief}\\n\\nStrategic Implications: {strategic_implications}\\n\\nRecommended Responses: {recommended_responses}\\n\\nAction Items: {action_items}\\n\\nFull archive in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1087. LOW-READABILITY TAKEOVER (8 nodes)
  // ===================================================================

  "Low-Readability Takeover": {
    summary:
      "Identifies competitor pages with poor readability as content takeover opportunities. Scans competitor pages via SerpAPI, scores readability in runCode, filters for low-readability targets via a condition, analyzes content structure with AI, generates superior content outlines, builds content briefs, ranks by priority, and assigns to the team via Slack.",
    nodes: [
      trigger("Webhook: Readability takeover scan request", "webhook_app", {
        source: "SEO / Content Tool",
        event: "readability.scan.requested",
        howItStarts:
          "A readability takeover scan is requested via webhook with target keywords and competitor domains to identify poorly-written competitor content that can be outranked with superior readability",
        setup:
          "Configure SerpAPI for competitor page scanning. Set up Google Sheets for content brief management. Add Slack for team assignments.",
        inputFields: [],
      }),

      // 2. Competitor page scan (SerpAPI)
      aiNode(
        "2",
        "AI: Extract scan parameters",
        280,
        "You are an SEO analyst. Extract the target keywords, competitor domains, and quality standards from the payload. Return JSON: {target_keyword: string, competitors: string, quality_threshold: string, content_standard: string}",
        "Readability scan request: {webhook_payload}. Extract keywords, competitors, and quality expectations.",
        [
          { field: "target_keyword", as: "target_keyword", type: "string" },
          { field: "competitors", as: "competitor_domains", type: "string" },
          {
            field: "quality_threshold",
            as: "quality_threshold",
            type: "string",
          },
          { field: "content_standard", as: "content_standard", type: "string" },
        ],
      ),

      // 3. SerpAPI competitor scan
      serpApiNode(
        "3",
        "SerpAPI: Scan competitor pages for keyword",
        520,
        "{target_keyword}",
        [
          {
            name: "organic_results",
            customName: "competitor_pages",
            type: "string",
          },
          {
            name: "organic_results.length",
            customName: "pages_found",
            type: "number",
          },
        ],
      ),

      // 4. Readability scorer (runCode)
      n("4", "runCode", "Score readability of competitor content", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score readability of competitor pages based on snippet analysis
const pages = JSON.parse(vars.competitor_pages || '[]');
const scored = pages.slice(0, 10).map((p, idx) => {
  const snippet = p.snippet || "";
  const title = p.title || "";
  const url = p.link || p.url || "";
  // Readability heuristics from snippet
  const sentences = snippet.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const words = snippet.split(/\\s+/);
  const avgSentenceLength = sentences.length > 0 ? words.length / sentences.length : 0;
  const longWords = words.filter(w => w.length > 12).length;
  const longWordPct = words.length > 0 ? (longWords / words.length * 100) : 0;
  // Lower score = harder to read = better opportunity
  let readabilityScore = 100;
  readabilityScore -= Math.min(30, avgSentenceLength * 1.5);
  readabilityScore -= Math.min(20, longWordPct * 2);
  readabilityScore = Math.max(0, Math.round(readabilityScore));
  const isLowReadability = readabilityScore < 50;
  return {
    title: title,
    url: url,
    position: idx + 1,
    readability_score: readabilityScore,
    avg_sentence_length: Math.round(avgSentenceLength),
    long_word_pct: longWordPct.toFixed(1),
    is_takeover_target: isLowReadability ? "yes" : "no",
    snippet_preview: snippet.substring(0, 100)
  };
});
const targets = scored.filter(s => s.is_takeover_target === "yes");
return {
  scored_pages_json: JSON.stringify(scored),
  total_scored: scored.length.toString(),
  takeover_targets_json: JSON.stringify(targets),
  takeover_count: targets.length.toString(),
  has_targets: targets.length > 0 ? "yes" : "no",
  best_target_url: targets[0]?.url || "none",
  best_target_score: targets[0]?.readability_score?.toString() || "N/A",
  best_target_position: targets[0]?.position?.toString() || "N/A"
};`,
          outputVariables: [
            { name: "scored_pages_json", type: "string" },
            { name: "total_scored", type: "string" },
            { name: "takeover_targets_json", type: "string" },
            { name: "takeover_count", type: "string" },
            { name: "has_targets", type: "string" },
            { name: "best_target_url", type: "string" },
            { name: "best_target_score", type: "string" },
            { name: "best_target_position", type: "string" },
          ],
        },
      }),

      // 5. Low-readability filter condition
      n("5", "condition", "Are there low-readability takeover targets?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{has_targets}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 6. Content structure analyzer (AI)
      aiNode(
        "6",
        "AI: Analyze competitor content weaknesses",
        1240,
        "You are a content quality analyst. Analyze the low-readability competitor pages and identify specific structural and writing weaknesses that can be exploited. For the top target, outline exactly how to create superior content: better structure, clearer language, better formatting, more comprehensive coverage. Return JSON: {weakness_analysis: string, superior_outline: string, recommended_format: string, target_word_count: string, readability_target: string, competitive_angles: string}",
        "Keyword: {target_keyword}. Takeover targets: {takeover_targets_json}. Best target: {best_target_url} (readability: {best_target_score}, position: #{best_target_position}). Quality standard: {content_standard}. How can we write something dramatically better?",
        [
          {
            field: "weakness_analysis",
            as: "weakness_analysis",
            type: "string",
          },
          { field: "superior_outline", as: "superior_outline", type: "string" },
          {
            field: "recommended_format",
            as: "recommended_format",
            type: "string",
          },
          {
            field: "target_word_count",
            as: "target_word_count",
            type: "string",
          },
          {
            field: "readability_target",
            as: "readability_target",
            type: "string",
          },
          {
            field: "competitive_angles",
            as: "competitive_angles",
            type: "string",
          },
        ],
      ),

      // 7. Priority ranker + content brief builder
      n("7", "runCode", "Build prioritized content briefs", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build content briefs prioritized by takeover potential
const targets = JSON.parse(vars.takeover_targets_json || '[]');
const briefs = targets.map((t, idx) => {
  const takeoverPotential = Math.round(100 - t.readability_score + (11 - t.position) * 5);
  return {
    rank: idx + 1,
    target_url: t.url,
    target_title: t.title,
    current_position: t.position,
    readability_score: t.readability_score,
    takeover_potential: Math.min(100, Math.max(0, takeoverPotential)),
    status: "assigned"
  };
});
briefs.sort((a, b) => b.takeover_potential - a.takeover_potential);
briefs.forEach((b, i) => b.rank = i + 1);
return {
  briefs_json: JSON.stringify(briefs.slice(0, 5)),
  total_briefs: Math.min(briefs.length, 5).toString(),
  top_target_title: briefs[0]?.target_title || "N/A",
  top_takeover_potential: briefs[0]?.takeover_potential?.toString() || "0",
  brief_summary: briefs.slice(0, 3).map(b => "#" + b.rank + ": " + b.target_title + " (potential: " + b.takeover_potential + ")").join(" | ")
};`,
          outputVariables: [
            { name: "briefs_json", type: "string" },
            { name: "total_briefs", type: "string" },
            { name: "top_target_title", type: "string" },
            { name: "top_takeover_potential", type: "string" },
            { name: "brief_summary", type: "string" },
          ],
        },
      }),

      // 8. Team assignment (Slack)
      slackNode(
        "8",
        "Assign takeover briefs to team (Slack)",
        1720,
        "LOW-READABILITY TAKEOVER OPPORTUNITIES\\n\\nKeyword: {target_keyword}\\nTargets Found: {takeover_count}\\nBriefs Created: {total_briefs}\\n\\nTop Target: {best_target_url}\\nReadability: {best_target_score}/100\\nPosition: #{best_target_position}\\nTakeover Potential: {top_takeover_potential}/100\\n\\nWeaknesses: {weakness_analysis}\\nFormat: {recommended_format}\\nWord Count Target: {target_word_count}\\nReadability Target: {readability_target}\\n\\nBriefs: {brief_summary}\\n\\nCompetitive Angles: {competitive_angles}\\nFull briefs in Google Sheets.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // True: targets found, analyze and create briefs
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
